import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import data from '@emoji-mart/data'
import ChatPageMobile from './chat-page-mobile';
import LanguageSelector from './components/LanguageSelector';
import CRSCalculator from './components/CRSCalculator';
import CRSResults from './components/CRSResults';
import OINPCalculator from './components/OINPCalculator';
import OINPResults from './components/OINPResults';
import OINPEmployerChecker from './components/OINPEmployerChecker';
import OINPEmployerResults from './components/OINPEmployerResults';
import AssessmentContactCapture from './components/AssessmentContactCapture';
import ExpertSidebar from './components/ExpertSidebar';
import ChatArea from './components/ChatArea';
import { stripEngineMarkers } from './chat-message';
import { createFunnelSession, markConversationStarted, markConversationFinished, markSessionDropped, getSessionId } from './services/funnelTracking';
import { saveAssessment, requestEmployerChecklist, registerNotifyMe, subscribeToDrawAlerts, requestConsultation, anonymousConsultation, employerCheckShareUrl } from './services/assessments';
import { buildCandidateTranscript, buildEmployerTranscript } from './assessments/oinp/transcript';
import { buildCandidateAiSummary } from './assessments/oinp/engine';
import { t as tOinp } from './assessments/oinp/i18n';
import { getInactivityMessage, firstMessage, CHOICE_PROMPT_BY_LANG, GREETINGS_BY_LANG, NEUTRAL_GREETINGS_BY_LANG } from './chatConstants';
import {
  WidgetWrapper, DesktopLayout, ChatAreaContainer,
  IFrameChatWindow, IframeHeader, LogoWrapper, Logo, ExitButtonWrapper,
  LoadingOverlay, FadeInWrapper,
  PopupOverlay, PopupContent, PopupButton,
  createMessagesHash,
} from './ChatStyles';

import ImavenIcon from './assets/imaven-icon.svg'
import LoadingIcon from './assets/load-line.svg'
import { resolveMarketplaceHandoff, primeStorageFromHandoff } from './services/marketplaceHandoff';

const ChatPage = () => {
    // `assessmentType` is only present on /assess/:apikey/:type — the advert
    // entry point. It runs the SAME widget, just starting at the questionnaire
    // instead of the identification chat, so an ad visitor gets the practice's
    // real branding, sidebar and layouts rather than a second, thinner UI.
    const { apikey, type: assessmentType } = useParams();
    const ASSESSMENT_PHASES = { oinp: 'oinp', crs: 'crs', 'oinp-employer': 'oinp_employer' };
    const assessmentEntryPhase = assessmentType ? (ASSESSMENT_PHASES[assessmentType] || 'oinp') : null;
    const isAssessmentEntry = Boolean(assessmentEntryPhase);
    // Set once /chat/start confirms the adviser's discipline offers this template.
    // A ref, not state, because handleLanguageSelect reads it in the same tick.
    const assessmentEntryAllowedRef = useRef(false);
    const [searchParams] = useSearchParams();
    const resumeConversationId = searchParams.get('resume');
    // Marketplace hand-off (imavenai.com). See services/marketplaceHandoff.js.
    const handoffToken = searchParams.get('handoff');
    const [messages, setMessages] = useState([]);
    const [currentMessage, setCurrentMessage] = useState("");
    const [error, setError] = useState("");
    // Determine if the component is rendered inside an iframe at the very first render
    const initialIsIframe = (typeof window !== 'undefined') && window.self !== window.top;
    const [isIframe, setIsIframe] = useState(initialIsIframe);
    const listRef = useRef(null);
    const inputRef = useRef(null);
    const uploadRef = useRef(null);
    const [loadingMessage, setLoadingMessage] = useState(false);
    const [loading, setLoading] = useState(true);
    const [emojiPickerOn, setEmojiPickerOn] = useState(false);
    // If we are inside an iframe we want the default to be light mode (darkMode = false)
    const [darkMode, setDarkMode] = useState(false);
    const [mongoColorScheme, setMongoColorScheme] = useState(null); // New state for MongoDB color scheme
    const [expertData, setExpertData] = useState(
        {
            expertName: '',
            imgUrl: '',
            expertTilte: '',
            stars: 0,
            reviewAmount: 0,
            greetingsMessage: '',
            expertInfo: {
                location: "",
                hourClose: "",
                site: "",
                email: "",
                phone: ""
            },
            widget: {
                colorScheme: "red", // Changed to red for testing
                logoUrl: "https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/bluelogo.svg"
            },
            workingHours: {
                isOpen: false,
                currentStatus: 'Hours not available',
                todayHours: null,
                workWeek: [],
                workStartHour: '09:00',
                workEndHour: '17:00',
                timezone: 'UTC',
                schedule: []
            }
        })
    const [isMobile, setIsMobile] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [popupData, setPopupData] = useState({ href: '', adviserId: '' });
    const [feedbackState, setFeedbackState] = useState('idle'); // idle | positive | negative | commenting | submitted
    const [feedbackComment, setFeedbackComment] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [transcriptionStatus, setTranscriptionStatus] = useState('');
    const [audioChunks, setAudioChunks] = useState([]);
    const [recordingMimeType, setRecordingMimeType] = useState('audio/webm');
    const [showInactivityPrompt, setShowInactivityPrompt] = useState(false);
    const [showAnimatedIcon, setShowAnimatedIcon] = useState(false);
    const [disableInactivityPrompt, setDisableInactivityPrompt] = useState(false);
    const inactivityTimerRef = useRef(null);
    const [showUserInactiveMessage, setShowUserInactiveMessage] = useState(false);
    const userInactivityTimerRef = useRef(null);
    const lastUserActivityRef = useRef(Date.now());
    const [inactivityStage, setInactivityStage] = useState(0);
    const lastFiredStageRef = useRef(0);
    const hasUserInteractedRef = useRef(false);
    const [conversationLanguage, setConversationLanguage] = useState('en');
    const conversationInitializedRef = useRef(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [errorPopupData, setErrorPopupData] = useState({ href: '', adviserId: '', errorType: '' });
    const consecutiveErrorsRef = useRef(0);

    // New Flow States: language_select → identification (chat) → choice → assessment | intake → results → intake
    // Assessment phases: crs | crs_results | oinp | oinp_results | oinp_employer | oinp_employer_results
    const [widgetPhase, setWidgetPhase] = useState('language_select');
    const [selectedLanguage, setSelectedLanguage] = useState('en');
    const [crsResult, setCrsResult] = useState(null);
    const [crsAnswers, setCrsAnswers] = useState(null);
    // OINP (Ontario Workforce Priority) assessment state
    const [oinpResult, setOinpResult] = useState(null);
    const [oinpAnswers, setOinpAnswers] = useState(null);
    const [employerResult, setEmployerResult] = useState(null);
    const [employerAnswers, setEmployerAnswers] = useState(null);
    // Assessment-first entry only: contact is asked for at the END, and only if
    // they choose to speak to an adviser.
    const [assessmentContact, setAssessmentContact] = useState({ name: '', email: '', phone: '' });
    const [assessmentContactNote, setAssessmentContactNote] = useState('');
    const [assessmentContactError, setAssessmentContactError] = useState('');
    const [assessmentContactSending, setAssessmentContactSending] = useState(false);
    // Id of the assessment stored while still anonymous. Conversion attaches the
    // conversation and contact to THIS record instead of creating a duplicate.
    const [anonymousAssessmentId, setAnonymousAssessmentId] = useState(null);
    const [identificationComplete, setIdentificationComplete] = useState(false);
    const pendingIntakeResponseRef = useRef(null);

    // ── Discipline-driven widget behaviour ────────────────────────────────────
    // Resolved server-side from the adviser's category/subcategory and the CRM's
    // per-category settings, and returned on /chat/start. The widget must never
    // decide on its own that CRS/OINP apply — those are Canadian-immigration
    // instruments and this widget serves every discipline.
    const [widgetConfig, setWidgetConfig] = useState(null);
    // Default to nothing until the server answers, so a slow/failed load shows no
    // assessment buttons rather than immigration ones to the wrong adviser.
    const assessmentTemplates = widgetConfig?.assessmentTemplates || [];
    const showAssessmentChoice = widgetConfig?.showAssessmentChoice === true && assessmentTemplates.length > 0;
    // Returning-user identity verification (OTP)
    const [otpError, setOtpError] = useState(false);
    const [otpSubmitting, setOtpSubmitting] = useState(false);
    const [diagnosticClassification, setDiagnosticClassification] = useState(null); // { result: 'viable'|'fixable-hot'|'fixable-cold'|'reject' }

    // Conversation Progress State
    const [conversationStep, setConversationStep] = useState('welcome');
    const [capturedInfo, setCapturedInfo] = useState({});
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    const [questionnaireProgress, setQuestionnaireProgress] = useState(null); // { current: X, total: Y }

    // Update conversation progress based on message count and content
    useEffect(() => {
        const messageCount = messages.filter(m => m.type === 'user').length;
        
        // Auto-progress through journey steps based on conversation state
        if (messageCount === 0) {
            setConversationStep('welcome');
        } else if (messageCount === 1) {
            setConversationStep('initial');
        } else if (messageCount >= 2 && uploadedDocuments.length === 0) {
            setConversationStep('gathering');
        } else if (uploadedDocuments.length > 0 && messageCount < 6) {
            setConversationStep('documents');
        } else if (messageCount >= 6) {
            setConversationStep('analysis');
        }
    }, [messages, uploadedDocuments]);

    // Extract captured info from conversation (basic extraction)
    useEffect(() => {
        const userMessages = messages.filter(m => m.type === 'user').map(m => m.message || '');
        const allText = userMessages.join(' ').toLowerCase();
        
        const extractedInfo = {};
        
        // Extract topic/service interest from first few messages
        if (userMessages.length > 0 && userMessages[0]) {
            const firstMsg = userMessages[0];
            if (firstMsg.length > 5 && !firstMsg.startsWith('File uploaded')) {
                extractedInfo.topic = firstMsg.slice(0, 50) + (firstMsg.length > 50 ? '...' : '');
            }
        }
        
        // Extract country mentions (use word boundary matching to avoid false positives like "vitaly" → "italy")
        const countries = ['usa', 'uk', 'israel', 'canada', 'germany', 'france', 'spain', 'italy', 'india', 'australia', 'china', 'brazil', 'mexico', 'japan'];
        for (const country of countries) {
            const regex = new RegExp(`\\b${country}\\b`, 'i');
            if (regex.test(allText)) {
                extractedInfo.country = country.charAt(0).toUpperCase() + country.slice(1);
                break;
            }
        }
        
        // Extract business type mentions
        const businessTypes = ['startup', 'small business', 'enterprise', 'corporation', 'llc', 'sole proprietor', 'partnership'];
        for (const bType of businessTypes) {
            if (allText.includes(bType)) {
                extractedInfo.businessType = bType.charAt(0).toUpperCase() + bType.slice(1);
                break;
            }
        }
        
        // Extract urgency
        if (allText.includes('urgent') || allText.includes('asap') || allText.includes('immediately')) {
            extractedInfo.urgency = 'High';
        }
        
        setCapturedInfo(prev => ({ ...prev, ...extractedInfo }));
    }, [messages]);

    const base_url = process.env.REACT_APP_API_BASE_URL

    const submitFeedback = async (rating, comment) => {
        try {
            const convoId = localStorage.getItem('imavenconvoid');
            await fetch(base_url + '/chat/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    conversationId: convoId,
                    expertId: expertData?.id || '',
                    rating,
                    comment: comment || undefined
                })
            });
        } catch (err) {
            console.error('Failed to submit feedback:', err);
        }
    };

    const handleFeedbackThumb = (rating) => {
        if (rating === 'positive') {
            submitFeedback('positive');
            setFeedbackState('positive');
            setTimeout(() => setFeedbackState('submitted'), 1500);
        } else {
            setFeedbackState('commenting');
        }
    };

    const handleFeedbackSubmit = (skip = false) => {
        if (!skip && feedbackComment.trim()) {
            submitFeedback('negative', feedbackComment.trim());
        } else {
            submitFeedback('negative');
        }
        setFeedbackState('submitted');
    };

    // Function to get chatbot icon based on color scheme - now uses MongoDB color scheme if available
    const getChatbotIcon = (colorScheme) => {
        const iconMap = {
            'blue': 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/CB_Logo_dblue.svg',
            'cyan': 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/CB_Logo_cyan.svg',
            'green': 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/CB_Logo_green.svg',
            'lightblue': 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/CB_Logo_lblue.svg',
            'magento': 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/CB_Logo_magento.svg',
            'orange': 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/CB_Logo_orange.svg',
            'red': 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/CB_Logo_red.svg'
        };
        return iconMap[colorScheme] || iconMap['blue']; // Default to blue if color scheme not found
    }

    // Get the current color scheme - prioritize MongoDB color scheme over expertData
    const getCurrentColorScheme = () => {
        return mongoColorScheme || expertData?.widget?.colorScheme || 'blue';
    }

    // Make color scheme available globally for other components
    useEffect(() => {
        if (window && typeof window === 'object') {
            window.getCurrentColorScheme = getCurrentColorScheme;
        }
        return () => {
            if (window && window.getCurrentColorScheme) {
                delete window.getCurrentColorScheme;
            }
        };
    }, [mongoColorScheme, expertData?.widget?.colorScheme]);

    // Add useEffect to get MongoDB color scheme after widget loads
    useEffect(() => {
        const getMongoColorScheme = () => {
            try {
                const timerId = setTimeout(() => {
                    if (window.Widget && typeof window.Widget.getColorScheme === 'function') {
                        const colorScheme = window.Widget.getColorScheme();
                        console.log('MongoDB Color Scheme retrieved:', colorScheme);
                        setMongoColorScheme(colorScheme);
                        
                        // Update chatbot launcher icon with new color scheme if in iframe
                        if (window.self !== window.top) {
                            const launcherIconUrl = getChatbotIcon(colorScheme);
                            window.parent.postMessage({ 
                                action: 'updateLauncherIcon', 
                                iconUrl: launcherIconUrl,
                                colorScheme: colorScheme
                            }, '*');
                        }
                    } else {
                        console.warn('Widget.getColorScheme() not available');
                    }
                }, 2000);
                return timerId;
            } catch (error) {
                console.error('Error getting MongoDB color scheme:', error);
                return null;
            }
        };

        const timerId = getMongoColorScheme();
        return () => {
            if (timerId) clearTimeout(timerId);
        };
    }, []);

    useEffect(() => {
        const isIframeMode = window.self !== window.top;
        setIsIframe(isIframeMode);
        
        // Set light mode for iframe (embedded chatbot), dark mode for standalone
        if (isIframeMode) {
            setDarkMode(false); // Light mode for iframe integration
        }
    }, []);

    // Add useEffect for mobile detection
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const toggleMode = () => {
        setDarkMode(!darkMode)
    }

    // Handler: user selects a language from the flags screen
    const handleLanguageSelect = (langCode) => {
        setSelectedLanguage(langCode);
        setConversationLanguage(langCode);

        // Advert entry: no identification chat, straight into the questionnaire —
        // but only if the server confirmed this adviser offers it.
        if (isAssessmentEntry && assessmentEntryAllowedRef.current) {
            setWidgetPhase(assessmentEntryPhase);
            return;
        }

        setWidgetPhase('identification');
        setLoadingMessage(true);
        
        // Assistant placeholder (fills in via updateLastMessage). Use 'gpt' so the greeting
        // renders as a normal left-aligned bubble + typing indicator, not a centered system pill.
        setMessages([{ message: '', type: 'gpt', timestamp: new Date().toLocaleString() }]);

        fetch(base_url + '/chat/sendMessage', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Conversation-ID': localStorage.getItem('imavenconvoid'),
                'X-Thread-ID': localStorage.getItem('imavenconvoid'),
                'X-Asst-ID': localStorage.getItem('asstId'),
                'X-Directive-ID': localStorage.getItem('directiveId'),
                'x-apikey': apikey,
                'X-Color-Scheme': getCurrentColorScheme(),
                'X-Language-Selection': langCode
            },
            // The opener is configurable per category/subcategory in the CRM: it is
            // what tells the AI whether to advertise a free CRS/OINP check or
            // something else entirely. Falls back to the neutral ask.
            body: JSON.stringify({ message: `[SYSTEM] User selected language: ${langCode}. Please greet the user in this language and ask for their full name, email address, and phone number.${widgetConfig?.greetingInstruction ? ' ' + widgetConfig.greetingInstruction : ''}` })
        })
        .then(async response => {
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Error');
            return data;
        })
        .then(res => {
            localStorage.setItem('asstId', res.asstId);
            localStorage.setItem('directiveId', res.directiveId);
            updateLastMessage(res.response);
        })
        .catch(err => {
            console.error('Error starting identification:', err);
            // Per-key fallback: the CRM greeting for this language, then its English,
            // then the widget's built-in default. So configuring only English still
            // leaves every other language working.
            const configured = widgetConfig?.greetings || {};
            // The built-in default advertises the free CRS/OINP checks by name, so it
            // is only correct for a discipline that actually offers them. Everyone
            // else gets the neutral wording rather than a promise we can't keep.
            const builtIn = assessmentTemplates.length > 0 ? GREETINGS_BY_LANG : NEUTRAL_GREETINGS_BY_LANG;
            const fallback = configured[langCode] || configured.en
                || builtIn[langCode] || builtIn.en;
            updateLastMessage(fallback);
        })
        .finally(() => {
            setLoadingMessage(false);
        });
    };

    // Maps an assessment template id to the widget phase that runs it. The server
    // sends ids; this is the only place that knows which screen each one opens.
    const ASSESSMENT_PHASE_BY_ID = { crs: 'crs', oinp: 'oinp', 'oinp-employer': 'oinp_employer' };

    /**
     * Start whichever assessment the visitor picked. Guarded against ids the server
     * offered but this widget build doesn't implement (independent deploys).
     */
    const handleChooseAssessment = (templateId) => {
        const phase = ASSESSMENT_PHASE_BY_ID[templateId];
        if (!phase) {
            console.warn('[widget] unknown assessment template:', templateId);
            return;
        }
        setMessages(prev => prev.filter(m => m.type !== 'choice_buttons'));
        setWidgetPhase(phase);
    };

    // Handler: user chooses to skip CRS and go straight to intake
    const handleChooseIntake = () => {
        setWidgetPhase('intake');
        setLoadingMessage(true);
        
        setMessages(prev => {
            const filtered = prev.filter(m => m.type !== 'choice_buttons');
            // Assistant placeholder — 'gpt' so the first intake reply renders as a normal
            // left-aligned bubble, not a centered system pill.
            return [...filtered, { message: '', type: 'gpt', timestamp: new Date().toLocaleString() }];
        });
        
        pendingIntakeResponseRef.current = null;
        
        fetch(base_url + '/chat/sendMessage', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Conversation-ID': localStorage.getItem('imavenconvoid'),
                'X-Thread-ID': localStorage.getItem('imavenconvoid'),
                'X-Asst-ID': localStorage.getItem('asstId'),
                'X-Directive-ID': localStorage.getItem('directiveId'),
                'x-apikey': apikey,
                'X-Color-Scheme': getCurrentColorScheme(),
                'X-Continue-Intake': 'true',
                ...(selectedLanguage ? { 'X-Language-Selection': selectedLanguage } : {})
            },
            body: JSON.stringify({ message: 'The user chose to continue with the consultation. Please begin.' })
        })
        .then(async response => {
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Error');
            return data;
        })
        .then(res => {
            localStorage.setItem('asstId', res.asstId);
            localStorage.setItem('directiveId', res.directiveId);
            updateLastMessage(res.response);

            if (res.progress && res.progress.current && res.progress.total) {
                setQuestionnaireProgress({
                    current: res.progress.current,
                    total: res.progress.total
                });
            }

            if (res.classification && res.classification.result) {
                setDiagnosticClassification(res.classification);
            }
        })
        .catch(err => {
            console.error('Error starting intake:', err);
            updateLastMessage('<p>Let\'s continue with your consultation. Please tell me more about your situation.</p>');
        })
        .finally(() => {
            setLoadingMessage(false);
        });
    };

    // Handler: CRS calculation is complete — show results
    const handleCRSComplete = (result, answers) => {
        setCrsResult(result);
        setCrsAnswers(answers);
        setWidgetPhase('crs_results');
        
        // Save CRS results to the conversation in the database
        const conversationId = localStorage.getItem('imavenconvoid');
        if (conversationId) {
            fetch(`${base_url}/chat/saveCRSResults`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Conversation-ID': conversationId,
                    'x-apikey': apikey
                },
                body: JSON.stringify({ crsScore: result.total, crsResult: result, crsAnswers: answers })
            }).catch(err => console.warn('Failed to save CRS results:', err));
        }
        
        // NOTE: this used to also PUT { crsScore } to /funnel/session/:id/contact.
        // That endpoint requires userEmail or userPhone and rejects anything else
        // with a 400, and FunnelSession has no crsScore field to write to anyway,
        // so the call never did anything. CRS results now persist through
        // /chat/saveCRSResults into the widgetassessments collection, which the
        // adviser dashboard reads. Removed rather than left as a dead request.
    };

    // Handler: user cancels CRS and goes back to choice buttons
    const handleCRSBack = () => {
        setWidgetPhase('identification');
        setMessages(prev => [...prev, { 
            type: 'choice_buttons', 
            timestamp: new Date().toLocaleString(),
            message: '',
            hideMessage: false
        }]);
    };

    // Handler: user continues from CRS results to intake
    const handleContinueToIntakeFromCRS = () => {
        // Advert entry has no conversation yet, so consultation goes through the
        // contact capture step first.
        if (isAssessmentEntry) {
            setWidgetPhase('assessment_capture');
            return;
        }
        setWidgetPhase('intake');
        setLoadingMessage(true);
        
        // Build context summary from CRS data to pass to the AI
        if (crsResult && crsAnswers) {
            const contextSummary = buildCRSContextSummary(crsResult, crsAnswers);
            const conversationId = localStorage.getItem('imavenconvoid');
            if (conversationId) {
                fetch(base_url + '/chat/sendMessage', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Conversation-ID': conversationId,
                        'X-Thread-ID': conversationId,
                        'X-Asst-ID': localStorage.getItem('asstId'),
                        'X-Directive-ID': localStorage.getItem('directiveId'),
                        'x-apikey': apikey,
                        'X-Color-Scheme': getCurrentColorScheme(),
                        'X-Continue-Intake': 'true',
                        'X-CRS-Context': 'true',
                        ...(selectedLanguage ? { 'X-Language-Selection': selectedLanguage } : {})
                    },
                    body: JSON.stringify({ 
                        message: `[CRS_CONTEXT] The user has completed a CRS evaluation. Here is a summary of what we already know:\n${contextSummary}\n\nCRS Score: ${crsResult.total}/1200\n\nPlease continue the intake process WITHOUT re-asking questions we already have answers for. The user was promised a bonus for continuing.`
                    })
                })
                .then(async response => {
                    const data = await response.json();
                    if (!response.ok) throw new Error(data.message || 'Error');
                    return data;
                })
                .then(res => {
                    localStorage.setItem('asstId', res.asstId || localStorage.getItem('asstId'));
                    localStorage.setItem('directiveId', res.directiveId || localStorage.getItem('directiveId'));
                    const msg = { message: res.response, type: 'gpt', timestamp: new Date().toLocaleString() };
                    const choicePrompts = Object.values(CHOICE_PROMPT_BY_LANG);
                    setMessages(prev => {
                        const filtered = prev.filter(m => 
                            m.type !== 'choice_buttons' && 
                            !(m.type === 'gpt' && choicePrompts.includes(m.message))
                        );
                        return [...filtered, msg];
                    });
                    if (res.detectedLanguage) setConversationLanguage(res.detectedLanguage);
                    if (res.progress && res.progress.current && res.progress.total) {
                        setQuestionnaireProgress({
                            current: res.progress.current,
                            total: res.progress.total
                        });
                    }
                })
                .catch((err) => {
                    console.error('Error starting intake from CRS:', err);
                    const msg = { message: '<p>Let\'s continue with your consultation. Please tell me more about your situation.</p>', type: 'gpt', timestamp: new Date().toLocaleString() };
                    setMessages(prev => [...prev, msg]);
                })
                .finally(() => {
                    setLoadingMessage(false);
                });
            }
        }
    };

    // Handler: user declines intake after CRS results
    const handleDeclineIntake = () => {
        // Mark as warm lead — they have contact info + CRS score
        const sessionId = getSessionId();
        if (sessionId) {
            fetch(`${base_url}/funnel/session/${sessionId}/dropped`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reason: 'declined_after_crs',
                    crsScore: crsResult?.total,
                    messageCount: messages.filter(m => m.type === 'user').length,
                    language: conversationLanguage
                })
            }).catch(() => {});
        }
        
        // Show a thank you message in the chat
        const thankYouMsg = {
            message: `<p><strong>${selectedLanguage === 'he' ? 'תודה' : selectedLanguage === 'ar' ? 'شكراً' : 'Thank you'}!</strong><br/><br/>${selectedLanguage === 'he' ? 'אחד מחברי הצוות שלנו ייצור איתך קשר בקרוב.' : selectedLanguage === 'ar' ? 'سيتواصل معك أحد أعضاء فريقنا قريباً.' : 'One of our team members will reach out to you shortly with personalized recommendations based on your CRS score.'}</p>`,
            type: 'system',
            timestamp: new Date().toLocaleString()
        };
        setMessages(prev => [...prev, thankYouMsg]);
        setWidgetPhase('intake'); // Show chat with the thank you message
    };

    // Build a human-readable summary of CRS answers for the AI context
    const buildCRSContextSummary = (result, answers) => {
        const lines = [];
        lines.push(`Marital Status: ${answers.maritalStatus || 'N/A'}`);
        lines.push(`Age: ${answers.age || 'N/A'}`);
        lines.push(`Education: ${answers.education || 'N/A'}`);
        lines.push(`First Language Test: ${answers.firstLangTest || 'N/A'}`);
        if (answers.firstLangScores) {
            lines.push(`  Speaking: ${answers.firstLangScores.speaking || 'N/A'}, Listening: ${answers.firstLangScores.listening || 'N/A'}, Reading: ${answers.firstLangScores.reading || 'N/A'}, Writing: ${answers.firstLangScores.writing || 'N/A'}`);
        }
        if (answers.hasSecondLang) {
            lines.push(`Second Language Test: ${answers.secondLangTest || 'N/A'}`);
        }
        lines.push(`Canadian Work Experience: ${answers.canadianWork || 0} years`);
        lines.push(`Foreign Work Experience: ${answers.foreignWork || 0} years`);
        if (answers.certQual) lines.push(`Has Certificate of Qualification: Yes`);
        if (answers.pnp) lines.push(`Has Provincial Nomination: Yes`);
        if (answers.sibling) lines.push(`Has Sibling in Canada: Yes`);
        if (result.hasSpouse) {
            lines.push(`Spouse Education: ${answers.spouseEducation || 'N/A'}`);
            lines.push(`Spouse Canadian Work: ${answers.spouseCanWork || 0} years`);
        }
        return lines.join('\n');
    };

    // =====================================================================
    // OINP — Ontario Workforce Priority assessment
    // =====================================================================

    // Return to the chat with the choice buttons re-offered.
    const returnToChoice = () => {
        setWidgetPhase('identification');
        setMessages(prev => [...prev, {
            type: 'choice_buttons',
            timestamp: new Date().toLocaleString(),
            message: '',
            hideMessage: false
        }]);
    };


    const handleOINPComplete = (result, answers) => {
        setOinpResult(result);
        setOinpAnswers(answers);
        setWidgetPhase('oinp_results');

        // The server recomputes from the raw answers and owns the stored record.
        // If its result differs from ours (rule tables updated server-side), the
        // server wins — the user should never be looking at a stale grid.
        saveAssessment({
            type: 'oinp_candidate', answers, result, language: selectedLanguage, apikey,
            source: isAssessmentEntry ? 'assessment-link' : 'widget',
        }).then(stored => {
            if (stored?.result) setOinpResult(stored.result);
            if (stored?.id) setAnonymousAssessmentId(stored.id);
        });
    };

    const handleOINPNotifyMe = (email) => {
        registerNotifyMe({ email, language: selectedLanguage, apikey });
    };

    const handleShareEmployerChecklist = () => {
        const url = employerCheckShareUrl(apikey);
        if (navigator?.clipboard?.writeText) navigator.clipboard.writeText(url).catch(() => {});
        // Drop the link into the transcript so the adviser can see it was sent
        // and can re-send it themselves later.
        setMessages(prev => [...prev, {
            type: 'system',
            message: `<p><strong>${tOinp(selectedLanguage, 'employerCtaShare')}</strong><br/><a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a></p>`,
            timestamp: new Date().toLocaleString()
        }]);
    };

    const handleEmployerComplete = (result, answers) => {
        setEmployerResult(result);
        setEmployerAnswers(answers);
        setWidgetPhase('oinp_employer_results');

        saveAssessment({
            type: 'oinp_employer', answers, result, language: selectedLanguage, apikey,
            source: isAssessmentEntry ? 'assessment-link' : 'widget',
        }).then(stored => {
            if (stored?.result) setEmployerResult(stored.result);
            if (stored?.id) setAnonymousAssessmentId(stored.id);
        });
    };

    const handleEmployerChecklistRequest = (email, result) => {
        requestEmployerChecklist({
            email,
            result,
            language: selectedLanguage,
            apikey,
            candidateName: capturedInfo?.name || null,
            checkerLink: employerCheckShareUrl(apikey),
            expertName: expertData?.expertName || null,
        });
    };

    const handleSubscribeToAlerts = (email, result) => {
        subscribeToDrawAlerts({ email, result, language: selectedLanguage, apikey });
    };

    // Branding for the printable documents — they get forwarded to employers and
    // kept on file, so they carry the adviser's letterhead rather than a generic
    // header.
    const assessmentBrand = {
        logoUrl: expertData?.widget?.logoUrl || expertData?.imgUrl || '',
        expertName: expertData?.expertName || '',
        expertTitle: expertData?.expertTilte || '',
        site: expertData?.expertInfo?.site || '',
        email: expertData?.expertInfo?.email || '',
        phone: expertData?.expertInfo?.phone || '',
        location: expertData?.expertInfo?.location || '',
    };

    /**
     * Continue to consultation after an assessment.
     *
     * A completed OINP assessment IS a completed questionnaire — the same thing
     * as a work-permit intake that ran to the end. So this does not resume the
     * chat and start asking more questions: it finishes the conversation exactly
     * the way CONVERSATION:[DONE] from the model does.
     *
     * The answers are written into the conversation as a real Q&A transcript
     * first, so the case the adviser opens contains the actual dialogue rather
     * than an empty thread with a score attached. The server then runs the same
     * completion pipeline (session finished, conversation stamped, case analysis
     * triggered) and the widget shows the same adviser link, feedback prompt and
     * booking popup.
     */
    const completeAssessmentAsConversation = async ({ isEmployer, note }) => {
        // Clear the "how would you like to continue?" prompt and its buttons
        // BEFORE the network work, not after. Handing the assessment over takes a
        // few seconds, and for that whole time the user was being shown the old
        // choice prompt again — which reads as "nothing happened, pick again".
        // If the adviser lookup then fails, it never clears at all.
        const choicePrompts = Object.values(CHOICE_PROMPT_BY_LANG);
        setMessages(prev => prev.filter(m =>
            m.type !== 'choice_buttons' &&
            !(m.type === 'gpt' && choicePrompts.includes(m.message))
        ));
        setWidgetPhase('intake');
        setLoadingMessage(true);

        let adviserId = null;
        try {
            const transcript = isEmployer
                ? buildEmployerTranscript(employerAnswers || {}, selectedLanguage)
                : buildCandidateTranscript(oinpAnswers || {}, selectedLanguage);

            // Writes the transcript into the conversation and runs the same
            // finish + auto-analysis pipeline a finished AI chat runs. It also
            // returns the adviser id, which is what the platform hand-off link is
            // built from — see completeConversation for why we don't look it up.
            const handover = await requestConsultation({
                transcript,
                note: note || null,
                language: selectedLanguage,
                apikey,
            });
            adviserId = handover?.adviserId || null;
        } catch (error) {
            console.warn('[Assessment] Could not hand the assessment over:', error?.message);
        } finally {
            setLoadingMessage(false);
        }

        // Same ending the model's [DONE] produces: adviser link, feedback prompt
        // and the popup through to account creation and booking.
        const shown = await completeConversation(adviserId);

        // If the adviser could not be resolved at all there is no link to give,
        // so at least confirm the hand-over rather than leaving a dead screen.
        if (!shown) {
            setMessages(prev => [...prev, {
                message: `<p><strong>${tOinp(selectedLanguage, 'consultHeading')}</strong><br/>${tOinp(selectedLanguage, 'consultDone')}</p>`,
                type: 'system',
                timestamp: new Date().toLocaleString(),
            }]);
        }
    };

    const handleContinueToIntakeFromOINP = (note) => {
        // Advert entry has no conversation and no contact details yet, so the
        // consultation choice routes through the capture step first.
        if (isAssessmentEntry) {
            setAssessmentContactNote(note || '');
            setWidgetPhase('assessment_capture');
            return;
        }
        completeAssessmentAsConversation({ isEmployer: false, note });
    };

    /**
     * Third option: go back to whatever they originally came for.
     *
     * The Ontario check is often a detour — someone asks about a work permit,
     * runs the score check out of curiosity, and still wants help with the work
     * permit. This resumes the main intake where it left off rather than closing
     * the conversation, and hands the assessment across as context so the AI does
     * not re-ask anything the wizard already collected.
     */
    const handleResumeOriginalGoal = () => {
        setWidgetPhase('intake');
        setLoadingMessage(true);

        const conversationId = localStorage.getItem('imavenconvoid');
        if (!conversationId) { setLoadingMessage(false); return; }

        const summary = oinpResult && oinpAnswers
            ? buildCandidateAiSummary(oinpAnswers, oinpResult)
            : '';

        fetch(base_url + '/chat/sendMessage', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Conversation-ID': conversationId,
                'X-Thread-ID': conversationId,
                'X-Asst-ID': localStorage.getItem('asstId'),
                'X-Directive-ID': localStorage.getItem('directiveId'),
                'x-apikey': apikey,
                'X-Color-Scheme': getCurrentColorScheme(),
                'X-Continue-Intake': 'true',
                'X-Assessment-Context': 'true',
                ...(selectedLanguage ? { 'X-Language-Selection': selectedLanguage } : {})
            },
            body: JSON.stringify({
                message: `[ASSESSMENT_CONTEXT] The user ran the Ontario Workforce Priority (OINP) check as a side enquiry and now wants to carry on with what they originally came for. Everything below is already known — do NOT ask for any of it again, and do NOT restart the Ontario assessment:\n${summary}\n\nPick the consultation back up from where it stopped, based on their original stated need.`
            })
        })
        .then(async response => {
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Error');
            return data;
        })
        .then(res => {
            localStorage.setItem('asstId', res.asstId || localStorage.getItem('asstId'));
            localStorage.setItem('directiveId', res.directiveId || localStorage.getItem('directiveId'));
            const choicePrompts = Object.values(CHOICE_PROMPT_BY_LANG);
            setMessages(prev => ([
                ...prev.filter(m =>
                    m.type !== 'choice_buttons' &&
                    !(m.type === 'gpt' && choicePrompts.includes(m.message))
                ),
                { message: res.response, type: 'gpt', timestamp: new Date().toLocaleString() },
            ]));
            if (res.detectedLanguage) setConversationLanguage(res.detectedLanguage);
            if (res.progress?.current && res.progress?.total) {
                setQuestionnaireProgress({ current: res.progress.current, total: res.progress.total });
            }
        })
        .catch(err => {
            console.error('Error resuming the original enquiry:', err);
            setMessages(prev => [...prev, {
                message: '<p>Let\'s carry on with your consultation. Please tell me more about your situation.</p>',
                type: 'gpt',
                timestamp: new Date().toLocaleString()
            }]);
        })
        .finally(() => setLoadingMessage(false));
    };

    const handleContinueToIntakeFromEmployer = (note) => {
        if (isAssessmentEntry) {
            setAssessmentContactNote(note || '');
            setWidgetPhase('assessment_capture');
            return;
        }
        completeAssessmentAsConversation({ isEmployer: true, note });
    };

    /**
     * Advert entry conversion: contact details -> a real case.
     *
     * This is the only moment a conversation exists for an advert visitor. The
     * server creates it, attaches the contact, writes the Q&A transcript and runs
     * the same finish + analysis pipeline every other completion path runs; then
     * the widget shows the same adviser link and booking popup a finished chat
     * shows.
     */
    const submitAssessmentContact = async () => {
        if (!assessmentContact.name.trim()) {
            setAssessmentContactError(tOinp(selectedLanguage, 'captureNameRequired'));
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(assessmentContact.email)) {
            setAssessmentContactError(tOinp(selectedLanguage, 'captureEmailRequired'));
            return;
        }
        setAssessmentContactError('');
        setAssessmentContactSending(true);

        const isEmployer = assessmentEntryPhase === 'oinp_employer';
        const transcript = isEmployer
            ? buildEmployerTranscript(employerAnswers || {}, selectedLanguage)
            : assessmentEntryPhase === 'crs'
                ? []
                : buildCandidateTranscript(oinpAnswers || {}, selectedLanguage);

        const response = await anonymousConsultation({
            type: isEmployer ? 'oinp_employer' : assessmentEntryPhase === 'crs' ? 'crs' : 'oinp_candidate',
            answers: isEmployer ? employerAnswers : assessmentEntryPhase === 'crs' ? crsAnswers : oinpAnswers,
            result: isEmployer ? employerResult : assessmentEntryPhase === 'crs' ? crsResult : oinpResult,
            transcript,
            note: assessmentContactNote,
            contact: assessmentContact,
            assessmentId: anonymousAssessmentId,
            language: selectedLanguage,
            apikey,
            source: searchParams.get('utm_source') || 'assessment-link',
        });

        setAssessmentContactSending(false);

        if (!response?.success) {
            setAssessmentContactError(tOinp(selectedLanguage, 'captureFailed'));
            return;
        }

        // The conversation now exists — record it so the rest of the widget
        // behaves exactly as it does for a chat-first visitor.
        if (response.conversationId) localStorage.setItem('imavenconvoid', response.conversationId);

        setWidgetPhase('intake');
        await completeConversation(response.adviserId);
    };

    const handleDeclineAfterOINP = () => {
        const sessionId = getSessionId();
        if (sessionId) {
            fetch(`${base_url}/funnel/session/${sessionId}/dropped`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reason: 'declined_after_oinp',
                    assessmentVerdict: oinpResult?.verdict || null,
                    assessmentScore: oinpResult?.total ?? null,
                    messageCount: messages.filter(m => m.type === 'user').length,
                    language: conversationLanguage
                })
            }).catch(() => {});
        }
        setMessages(prev => [...prev, {
            message: `<p><strong>${selectedLanguage === 'he' ? 'תודה' : selectedLanguage === 'ar' ? 'شكراً' : 'Thank you'}!</strong><br/><br/>${tOinp(selectedLanguage, 'declineNote')}</p>`,
            type: 'system',
            timestamp: new Date().toLocaleString()
        }]);
        setWidgetPhase('intake');
    };

    // One place that maps an OINP phase to its screen, so the iframe, mobile and
    // desktop layouts can't drift apart as phases are added.
    const renderOinpPhase = () => {
        const scheme = getCurrentColorScheme();
        switch (widgetPhase) {
            case 'oinp':
                return (
                    <OINPCalculator
                        onComplete={handleOINPComplete}
                        onBack={returnToChoice}
                        onNotifyMe={handleOINPNotifyMe}
                        scheme={scheme}
                        language={selectedLanguage}
                    />
                );
            case 'oinp_results':
                return (
                    <OINPResults
                        result={oinpResult}
                        onContinueToIntake={handleContinueToIntakeFromOINP}
                        onDecline={handleDeclineAfterOINP}
                        // Offer the employer check only when it is one of this
                        // adviser's enabled templates.
                        onCheckEmployer={assessmentTemplates.includes('oinp-employer')
                            ? () => handleChooseAssessment('oinp-employer')
                            : null}
                        onShareEmployerChecklist={handleShareEmployerChecklist}
                        onSubscribeToAlerts={handleSubscribeToAlerts}
                        // Two options, not three, on an advert entry: there was no
                        // original question to carry on with.
                        onResumeOriginalGoal={isAssessmentEntry ? null : handleResumeOriginalGoal}
                        brand={assessmentBrand}
                        scheme={scheme}
                        language={selectedLanguage}
                    />
                );
            case 'assessment_capture':
                return (
                    <AssessmentContactCapture
                        contact={assessmentContact}
                        onChange={setAssessmentContact}
                        onSubmit={submitAssessmentContact}
                        onBack={() => setWidgetPhase(
                            assessmentEntryPhase === 'oinp_employer' ? 'oinp_employer_results'
                                : assessmentEntryPhase === 'crs' ? 'crs_results' : 'oinp_results'
                        )}
                        sending={assessmentContactSending}
                        error={assessmentContactError}
                        expertName={expertData?.expertName || ''}
                        scheme={scheme}
                        language={selectedLanguage}
                    />
                );
            case 'oinp_employer':
                return (
                    <OINPEmployerChecker
                        onComplete={handleEmployerComplete}
                        // Back goes to wherever they came from: their own results
                        // if they ran the candidate check first, else the choices.
                        onBack={oinpResult ? () => setWidgetPhase('oinp_results') : returnToChoice}
                        noc={oinpAnswers?.noc || null}
                        scheme={scheme}
                        language={selectedLanguage}
                    />
                );
            case 'oinp_employer_results':
                return (
                    <OINPEmployerResults
                        result={employerResult}
                        onRequestChecklist={handleEmployerChecklistRequest}
                        onSendToAdviser={handleContinueToIntakeFromEmployer}
                        onContinueToIntake={handleContinueToIntakeFromEmployer}
                        brand={assessmentBrand}
                        scheme={scheme}
                        language={selectedLanguage}
                    />
                );
            default:
                return null;
        }
    };
    const oinpPhaseScreen = renderOinpPhase();

    useEffect(() => {
        if (apikey) {
            let cancelled = false;

            const handleEmojiMessage = (event) => {
                if (event.data.action === 'insertEmoji') {
                    const emoji = event.data.emoji;
                    setCurrentMessage((prevMessage) => prevMessage + emoji);
                }
            };

            window.addEventListener('message', handleEmojiMessage);
            
            // Resume an existing conversation if ?resume=conversationId is in the URL
            const resumeConversation = async () => {
                try {
                    const data = await fetch(base_url + '/chat/resume/' + resumeConversationId, {
                        method: 'GET',
                        headers: {
                            'x-apikey': apikey,
                            'X-Color-Scheme': expertData?.widget?.colorScheme || 'blue'
                        }
                    });
                    
                    if (cancelled) return false;
                    
                    if (!data.ok) {
                        console.warn('[Resume] Failed to load conversation, starting fresh');
                        return false;
                    }
                    
                    const result = await data.json();
                    
                    if (cancelled) return false;
                    
                    if (!result.isResumed || !result.messages || result.messages.length === 0) {
                        return false;
                    }
                    
                    // Add a resume indicator message at the end
                    const resumedMessages = [
                        ...result.messages,
                        { 
                            message: '<p><em>Your conversation has been restored. You can continue from where you left off.</em></p>',
                            type: 'system',
                            timestamp: new Date().toLocaleString(),
                            isResumeIndicator: true
                        }
                    ];
                    
                    setMessages(resumedMessages);
                    setExpertData(result.expertData);
                    setWidgetConfig(result.expertData?.widgetConfig || null);
                    // Same gate as the fresh-start path: a resumed session opened via
                    // an /assess/... link must still confirm the adviser offers that
                    // questionnaire before handleLanguageSelect routes into it.
                    {
                        const rcfg = result.expertData?.widgetConfig;
                        assessmentEntryAllowedRef.current = isAssessmentEntry
                            && rcfg?.allowAssessmentEntryLinks === true
                            && (rcfg?.assessmentTemplates || []).includes(assessmentType);
                    }
                    setLoading(false);
                    
                    localStorage.setItem('imavenconvoid', result.conversationId);
                    localStorage.setItem('asstId', result.asstId);
                    localStorage.setItem('directiveId', result.directiveId);
                    
                    // Set conversation step based on message count (already past identification)
                    setConversationStep('questions');
                    
                    console.log(`[Resume] Conversation restored with ${result.messages.length} messages`);
                    
                    sendMessageToPlausible('ChatResumed', {
                        conversationId: result.conversationId,
                        messageCount: result.messages.length,
                        windowType: isIframe ? 'iframe' : 'window'
                    });
                    
                    // Send launcher icon to parent
                    if (window.self !== window.top) {
                        const colorScheme = mongoColorScheme || result.expertData?.widget?.colorScheme || 'blue';
                        const launcherIconUrl = getChatbotIcon(colorScheme);
                        window.parent.postMessage({ 
                            action: 'updateLauncherIcon', 
                            iconUrl: launcherIconUrl,
                            colorScheme: colorScheme
                        }, '*');
                    }
                    
                    return true;
                } catch (error) {
                    console.warn('[Resume] Error resuming conversation:', error);
                    return false;
                }
            };
            
            // Marketplace hand-off: the conversation already exists and the visitor is
            // identified, so skip language selection + identification and start the
            // intake the same way the "Continue" button does.
            const openHandoff = async () => {
                const h = await resolveMarketplaceHandoff({
                    baseUrl: base_url, apikey, token: handoffToken,
                    colorScheme: expertData?.widget?.colorScheme
                });
                if (cancelled || !h) return false;
                primeStorageFromHandoff(h);
                setExpertData(h.expertData);
                setWidgetConfig(h.expertData?.widgetConfig || null);
                assessmentEntryAllowedRef.current = false;
                const lang = h.language || 'en';
                setSelectedLanguage(lang);
                setConversationLanguage(lang);
                setIdentificationComplete(true);
                setLoading(false);
                // Show what the visitor wrote on the marketplace; the adviser's first
                // reply is appended by handleChooseIntake.
                setMessages(h.needText
                    ? [{ message: h.needText, type: 'user', timestamp: new Date().toLocaleString() }]
                    : []);
                handleChooseIntake();
                return true;
            };

            const fetchData = async () => {
                // Prevent re-initialization when mongoColorScheme changes
                if (conversationInitializedRef.current) return;

                if (handoffToken) {
                    const opened = await openHandoff();
                    if (opened || cancelled) {
                        if (opened) conversationInitializedRef.current = true;
                        return;
                    }
                }

                // Try to resume if URL param is present
                if (resumeConversationId) {
                    const resumed = await resumeConversation();
                    if (resumed || cancelled) {
                        if (resumed) conversationInitializedRef.current = true;
                        return;
                    }
                }
                
                try {
                    let visitorId = localStorage.getItem('imaven_visitor_id');
                    if (!visitorId) {
                        visitorId = 'v_' + Date.now().toString(36) + '_' + Math.random().toString(36).substring(2, 9);
                        localStorage.setItem('imaven_visitor_id', visitorId);
                    }

                    const data = await fetch(base_url + '/chat/start', {
                        method: 'GET', headers: {
                            'x-apikey': apikey,
                            'X-Color-Scheme': expertData?.widget?.colorScheme || 'blue',
                            'X-Visitor-Id': visitorId,
                            // Advert entry: fetch the adviser's branding but do not
                            // create a conversation. One is created only if the
                            // visitor asks to speak to someone.
                            ...(isAssessmentEntry ? { 'X-No-Conversation': 'true' } : {})
                        }
                    })

                    if (cancelled) return;

                    if (!data.ok) {
                        throw new Error('Failed to fetch data');
                    }

                    const result = await data.json();

                    if (cancelled) return;

                    // Don't show greeting yet — we show it after language selection
                    // Store the greeting for later use
                    const greetings = result.expertData && result.expertData.greetingsMessage && result.expertData.greetingsMessage.trim()
                        ? result.expertData.greetingsMessage
                        : firstMessage;

                    // Initialize with empty messages — greeting will come after language selection
                    setMessages([])

                    sendMessageToPlausible('ChatStarted', {
                        conversationId: result.response,
                        timeOfDay: String(new Date().getHours()).padStart(2, '0'),
                        windowType: isIframe ? 'iframe' : 'window',
                        apikey: apikey
                    })

                    setLoading(false)
                    // An advert visitor whose language is already in the link goes
                    // straight into the questionnaire; otherwise pick a language
                    // first, which the rest of the flow needs anyway.
                    // An /assess/... advert link is only honoured when the adviser's
                    // discipline actually offers that questionnaire. Otherwise the
                    // visitor falls back to the normal chat rather than seeing a
                    // Canadian immigration form on, say, a tax adviser's widget.
                    const cfg = result.expertData?.widgetConfig;
                    const entryAllowed = isAssessmentEntry
                        && cfg?.allowAssessmentEntryLinks === true
                        && (cfg?.assessmentTemplates || []).includes(assessmentType);
                    if (isAssessmentEntry && !entryAllowed) {
                        console.warn(`[widget] assessment entry "${assessmentType}" not available for this adviser — falling back to chat`);
                    }

                    if (entryAllowed) {
                        const urlLang = searchParams.get('lang');
                        if (urlLang) {
                            setSelectedLanguage(urlLang);
                            setConversationLanguage(urlLang);
                            setWidgetPhase(assessmentEntryPhase);
                        } else {
                            setWidgetPhase('language_select');
                        }
                    } else {
                        setWidgetPhase('language_select');
                    }
                    assessmentEntryAllowedRef.current = entryAllowed;

                    if (result.response) localStorage.setItem('imavenconvoid', result.response)
                    localStorage.setItem('asstId', 'asst_5WqDk3WVgQCoOlJO18quwhlh');
                    localStorage.setItem('directiveId', '67be323b44fccc7abd5b7a08');
                    // Store original greeting for fallback
                    localStorage.setItem('originalGreeting', greetings);

                    setExpertData(result.expertData)
                    setWidgetConfig(result.expertData?.widgetConfig || null);
                    conversationInitializedRef.current = true;
                    
                    const expertId = result.expertData?.id || result.expertData?.expertId || result.expertData?._id;
                    if (expertId) {
                        createFunnelSession(expertId).catch(err => {
                            console.warn('[Funnel] Failed to create session:', err);
                        });
                    }
                    
                    if (window.self !== window.top) {
                        const colorScheme = mongoColorScheme || result.expertData?.widget?.colorScheme || 'blue';
                        const launcherIconUrl = getChatbotIcon(colorScheme);
                        window.parent.postMessage({ 
                            action: 'updateLauncherIcon', 
                            iconUrl: launcherIconUrl,
                            colorScheme: colorScheme
                        }, '*');
                    }
                } catch (error) {
                    if (cancelled) return;
                    setError(error);
                    setLoading(false)
                    return
                }
            };

            fetchData();

            return () => {
                cancelled = true;
                window.removeEventListener('message', handleEmojiMessage);
            };
        }
    }, [mongoColorScheme]); // Add mongoColorScheme as dependency

    const messagesHash = createMessagesHash(messages);

    // useEffect(() => {
    //     if (listRef && listRef.current && !isIframe) {
    //         setTimeout(() => {
    //             listRef.current.scrollTop = listRef.current.scrollHeight;
    //         }, 10);
    //     }
    // }, [messagesHash, isIframe]);

    const sendMessageToPlausible = (type, props) => {
        if (window.plausible) {
            window.plausible(type, {
                props: {
                    ...props
                }
            });
        }
    }

    // Auto-extract and store contact info from user messages for drop recovery
    const tryExtractContactInfo = (message) => {
        const emailMatch = message.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
        const phoneMatch = message.match(/(?:\+?\d{1,3}[-.\s]?)?\(?\d{2,4}\)?[-.\s]?\d{3,4}[-.\s]?\d{3,4}/);
        
        if (emailMatch || phoneMatch) {
            const sessionId = getSessionId();
            if (sessionId) {
                const payload = {};
                if (emailMatch) payload.userEmail = emailMatch[0];
                if (phoneMatch) payload.userPhone = phoneMatch[0];
                
                fetch(`${base_url}/funnel/session/${sessionId}/contact`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                }).catch(() => {});
            }
        }
    };

    // ============================================
    // RETURNING-USER IDENTITY VERIFICATION (OTP)
    // ============================================
    const verifyIdentityRequest = (body) =>
        fetch(base_url + '/chat/verifyIdentity', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-apikey': apikey,
            },
            body: JSON.stringify({ conversationId: localStorage.getItem('imavenconvoid'), ...body })
        }).then(async r => {
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || 'Verification failed');
            return data;
        });

    const submitOtp = (code) => {
        setOtpSubmitting(true);
        setOtpError(false);
        verifyIdentityRequest({ code })
            .then(res => {
                if (!res.verified) {
                    setOtpError(true);
                    return;
                }
                // Success: drop the OTP box, switch to consultation mode, show recap.
                if (res.directiveId) localStorage.setItem('directiveId', res.directiveId);
                if (res.asstId) localStorage.setItem('asstId', res.asstId);
                setIdentificationComplete(true);
                setWidgetPhase('intake');
                setMessages(prev => ([
                    ...prev.filter(m => m.type !== 'otp_input'),
                    { message: res.response, type: 'gpt', timestamp: new Date().toLocaleString() }
                ]));
            })
            .catch(() => setOtpError(true))
            .finally(() => setOtpSubmitting(false));
    };

    const resendOtp = () => {
        setOtpSubmitting(true);
        setOtpError(false);
        verifyIdentityRequest({ resend: true })
            .catch(() => {})
            .finally(() => setOtpSubmitting(false));
    };

    const skipOtp = () => {
        setOtpSubmitting(true);
        verifyIdentityRequest({ skip: true })
            .then(res => {
                if (res.directiveId) localStorage.setItem('directiveId', res.directiveId);
                setMessages(prev => ([
                    ...prev.filter(m => m.type !== 'otp_input'),
                    { message: res.response, type: 'gpt', timestamp: new Date().toLocaleString() }
                ]));
            })
            .catch(() => {})
            .finally(() => setOtpSubmitting(false));
    };

    const otpProps = { error: otpError, submitting: otpSubmitting, onSubmit: submitOtp, onResend: resendOtp, onSkip: skipOtp };

    const sendMessage = () => {
        if (currentMessage.trim().length < 2)
            return
        
        // Try to extract contact info for drop recovery
        tryExtractContactInfo(currentMessage);
        
        // Check if user wants to finish the conversation
        const lowerCaseMessage = currentMessage.trim().toLowerCase();
        const finishPhrases = [
            "i want to finish this conversation",
            "finish conversation",
            "end conversation",
            "connect with adviser",
            "connect with advisor",
            "talk to human"
        ];
        
        const isFinishRequest = finishPhrases.some(phrase => lowerCaseMessage.includes(phrase));
        
        // Reset inactivity timer when user sends a message
        updateUserActivityTimestamp();

        // First remove any inactivity messages before adding the new message
        const filteredMessages = messages.filter(msg => !msg.isInactivityPrompt);
        
        // Handle request to finish conversation
        if (isFinishRequest) {
            // Update progress to connect step
            setConversationStep('connect');
            
            // Special handling for finishing conversation
            setLoadingMessage(true);
            const newMessage = { message: currentMessage, type: 'user', timestamp: new Date().toLocaleString() };
            let temp = [...filteredMessages, newMessage];
            setMessages(temp);
            setCurrentMessage("");
            
            // Add system message indicating we're connecting to adviser
            const connectionMessage = { 
                message: `<p>Thank you for your request. I'll help you connect with a human adviser right away.</p>
                          <p>Just a moment while I prepare your information...</p>`,
                type: 'system', 
                timestamp: new Date().toLocaleString(),
                isConnectionMessage: true
            };
            temp.push(connectionMessage);
            setMessages(temp);
            
            // Force finish flag for backend
            setTimeout(() => {
                fetch(base_url + '/chat/sendMessage', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Conversation-ID': localStorage.getItem('imavenconvoid'),
                        'X-Thread-ID': localStorage.getItem('imavenconvoid'),
                        'X-Asst-ID': localStorage.getItem('asstId'),
                        'X-Directive-ID': localStorage.getItem('directiveId'),
                        'x-apikey': apikey,
                        'X-Force-Finish': 'true', // Special header to indicate finish
                        'X-Color-Scheme': getCurrentColorScheme() // Add color scheme to headers
                    },
                    body: JSON.stringify({ message: "Request to connect with human adviser" })
                })
                .then(async response => {
                    const data = await response.json();
                    if (!response.ok) {
                        throw new Error(data.message || 'Error sending message. Please try again.');
                    }
                    return data;
                })
                .then(res => {
                    localStorage.setItem('asstId', res.asstId || localStorage.getItem('asstId'));
                    localStorage.setItem('directiveId', res.directiveId || localStorage.getItem('directiveId'));
                    
                    // Create generic link to continue to platform (meeting settings are checked on platform side)
                    const lastUrl = `
                    <p>
                      Thank you! 
                      <a
                        id="meeting-link"
                        data-adviserid="${res.id}"
                        data-threadid="${localStorage.getItem('imavenconvoid')}"
                        data-href="https://platform.imavenai.com/setMetting/${res.id}/${localStorage.getItem('imavenconvoid')}?lang=${res.detectedLanguage || 'en'}"
                        style="text-decoration: underline; cursor: pointer; font-weight: 500;"
                      >
                        Click here to continue
                      </a>
                      to proceed to the next step.
                    </p>`;

                    // Show popup
                    setPopupData({
                        href: `https://platform.imavenai.com/setMetting/${res.id}/${localStorage.getItem('imavenconvoid')}?lang=${res.detectedLanguage || 'en'}`,
                        adviserId: res.id
                    });
                    setShowPopup(true);

                    const lastMessage = { message: lastUrl, type: 'system', timestamp: new Date().toLocaleString() };
                    
                    setMessages((prevMessages) => prevMessages.filter(msg => !msg.isConnectionMessage).concat(lastMessage));
                    setLoadingMessage(false);
                })
                .catch(error => {
                    handleError(error);
                    console.log(error);
                    
                    const systemMessage = `
                        <p>
                            Oops! It looks like we're having some technical difficulties connecting you with an adviser.
                            Please try again in a moment.
                            Thank you for your patience!
                        </p>
                    `;
                    const newSystemMessage = {
                        message: systemMessage, 
                        type: "system",
                        timestamp: new Date().toLocaleString()
                    };

                    setMessages(prev => prev.filter(msg => !msg.isConnectionMessage).concat(newSystemMessage));
                    setLoadingMessage(false);
                });
            }, 1500);
            
            return;
        }

        if (window.plausible) {
            const now = new Date();
            const paddedHour = String(now.getHours()).padStart(2, '0');
            window.plausible('ChatMessageSent', {
                props: {
                    messageLength: currentMessage.length,
                    isQuestion: currentMessage.endsWith('?'),
                    timeOfDay: paddedHour,
                    messageCount: filteredMessages.length
                },
            });
        }
        
        // Mark conversation as started in funnel tracking (only on first user message)
        const userMessageCount = filteredMessages.filter(m => m.type === 'user').length;
        if (userMessageCount === 0) {
            const conversationId = localStorage.getItem('imavenconvoid');
            if (conversationId) {
                markConversationStarted(conversationId).catch(err => {
                    console.warn('[Funnel] Failed to mark conversation started:', err);
                });
            }
        }

        setLoadingMessage(true);
        const newMessage = { message: currentMessage, type: 'user', timestamp: new Date().toLocaleString() };
        let temp = [...filteredMessages, newMessage]
        setMessages(temp)
        setCurrentMessage("")

        const gptMessage = { message: "", type: 'gpt', timestamp: new Date().toLocaleString() };
        temp.push(gptMessage)
        setMessages(temp)

        setTimeout(() => {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }, 0);

        fetch(base_url + '/chat/sendMessage', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Conversation-ID': localStorage.getItem('imavenconvoid'),
                'X-Thread-ID': localStorage.getItem('imavenconvoid'),
                'X-Asst-ID': localStorage.getItem('asstId'),
                'X-Directive-ID': localStorage.getItem('directiveId'),
                'x-apikey': apikey,
                'X-Color-Scheme': getCurrentColorScheme(),
                ...(selectedLanguage ? { 'X-Language-Selection': selectedLanguage } : {})
            },
            body: JSON.stringify({ message: currentMessage })
        })
            .then(async response => {
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.message || 'Error sending message. Please try again.');
                }
                return data;
            })
            .then(res => {
                localStorage.setItem('asstId', res.asstId);
                localStorage.setItem('directiveId', res.directiveId);

                // Returning-user recognized: pause intake, show the OTP entry inline.
                if (res.awaitingVerification) {
                    updateLastMessage(res.response);
                    setOtpError(false);
                    setMessages(prev => [...prev, {
                        type: 'otp_input',
                        maskedEmail: res.maskedEmail,
                        message: '',
                        hideMessage: false,
                        timestamp: new Date().toLocaleString()
                    }]);
                    setLoadingMessage(false);
                    return;
                }

                // Detect identification complete: show inline choice buttons in chat
                if (res.identificationComplete && widgetPhase === 'identification' && !identificationComplete) {
                    pendingIntakeResponseRef.current = res.response;
                    setIdentificationComplete(true);

                    // No assessments for this discipline => no choice to offer. Go
                    // straight into intake: the server already ran the adviser's first
                    // intake turn in this same request, so this reply IS the first intake
                    // question (no dead end waiting for the visitor to type again). Fall
                    // through so its progress / language / finish flags are applied like
                    // any other turn.
                    if (!showAssessmentChoice) {
                        setWidgetPhase('intake');
                        updateLastMessage(res.response);
                    } else {
                        const thankYou = CHOICE_PROMPT_BY_LANG[selectedLanguage] || CHOICE_PROMPT_BY_LANG.en;
                        updateLastMessage(thankYou);

                        setTimeout(() => {
                            setMessages(prev => [...prev, {
                                type: 'choice_buttons',
                                timestamp: new Date().toLocaleString(),
                                message: '',
                                hideMessage: false
                            }]);
                            setLoadingMessage(false);
                        }, 800);
                        return;
                    }
                } else {
                    updateLastMessage(res.response);
                }

                // Capture detected language from server (set during directive switch or conversation end)
                if (res.detectedLanguage && res.detectedLanguage !== 'en') {
                    setConversationLanguage(res.detectedLanguage);
                }

                // Update questionnaire progress if available from server
                if (res.progress && res.progress.current && res.progress.total) {
                    setQuestionnaireProgress({
                        current: res.progress.current,
                        total: res.progress.total
                    });
                }

                // Handle diagnostic classification from server
                if (res.classification && res.classification.result) {
                    setDiagnosticClassification(res.classification);
                    console.log('[Diagnostic] Classification received:', res.classification.result);
                    
                    // For viable and fixable-hot, add booking button after the classification message
                    if (res.classification.result === 'viable' || res.classification.result === 'fixable-hot') {
                        setTimeout(() => {
                            const bookingMessage = {
                                type: 'booking_prompt',
                                timestamp: new Date().toLocaleString(),
                                message: '',
                                hideMessage: false,
                                classificationResult: res.classification.result
                            };
                            setMessages(prev => [...prev, bookingMessage]);
                        }, 1200);
                    }
                }

                // Reset consecutive errors counter on successful response
                consecutiveErrorsRef.current = 0;

                setTimeout(() => {
                    listRef.current.scrollTop = listRef.current.scrollHeight;
                    // Only focus the input on non-mobile devices
                    if (!isMobile) {
                        inputRef.current.focus();
                    }
                }, 10);

                if (res.finishFlag) {
                    // Mark conversation as finished in funnel tracking
                    markConversationFinished().catch(err => {
                        console.warn('[Funnel] Failed to mark conversation finished:', err);
                    });

                    // Marketplace (imavenai.com) embeds this widget for AI consultations and
                    // advances to the unlock step when the intake completes.
                    if (window.self !== window.top) {
                        try {
                            window.parent.postMessage({ type: 'imaven:intake-finished', conversationId: localStorage.getItem('imavenconvoid') }, '*');
                        } catch (e) { /* ignore */ }
                    }

                    // The adviser is already in this response, so go straight to the
                    // shared completion UI rather than looking them up again.
                    stopActivityMonitoring();
                    showConversationCompleteUi(res.id, res.detectedLanguage);
                }

            })
            .catch(error => {
                handleError(error);
                console.log(error);
                updateLastMessage(error)

                const systemMessage = `
                    <p>
                        Oops! It looks like we're having some technical difficulties right now.
                        Please give it a moment and try continuing with the conversation shortly.
                        Thank you for your patience!
                    </p>
                `
                const newSystemMessage = {
                    message: systemMessage, type: "system",
                    timestamp: new Date().toLocaleString()
                };

                let temp = [...messages, newSystemMessage]
                setMessages(temp)
            })
            .finally(() => {
                setLoadingMessage(false)
            })
    }

    // ---------------------------------------------------------------------
    // Conversation completion
    // ---------------------------------------------------------------------
    // Everything that has to happen when a conversation is DONE, in one place:
    // stop monitoring the user for inactivity, post the adviser link and the
    // feedback prompt, and open the "one last step" popup.
    //
    // There are now three ways to reach completion — the model emitting
    // CONVERSATION:[DONE], the finishFlag on a send response, and a finished
    // assessment whose owner asked to continue to consultation. They must all
    // land the user in the same place, so they all call this.

    /** Silence the inactivity nudges — the conversation is over. */
    const stopActivityMonitoring = () => {
        clearInterval(userInactivityTimerRef.current);
        clearTimeout(inactivityTimerRef.current);
        document.removeEventListener('keydown', handleKeyboardActivity);
        setDisableInactivityPrompt(true);
        hasUserInteractedRef.current = false;
        setShowUserInactiveMessage(false);
        setShowAnimatedIcon(false);
        setShowInactivityPrompt(false);
    };

    /**
     * Post the completion UI for a known adviser: the booking link, the feedback
     * request, and the popup.
     */
    const showConversationCompleteUi = (adviserId, detectedLanguage) => {
        const conversationId = localStorage.getItem('imavenconvoid');
        const lang = detectedLanguage || selectedLanguage || 'en';
        const href = `https://platform.imavenai.com/setMetting/${adviserId}/${conversationId}?lang=${lang}`;

        const lastUrl = `
        <p>
          Thank you!
          <a
            id="meeting-link"
            data-adviserid="${adviserId}"
            data-threadid="${conversationId}"
            data-href="${href}"
            style="text-decoration: underline; cursor: pointer; font-weight: 500;"
          >
            Click here to continue
          </a>
          to proceed to the next step.
        </p>`;

        setPopupData({ href, adviserId });

        const linkMessage = { message: lastUrl, type: 'system', timestamp: new Date().toLocaleString() };
        const feedbackMsg = { message: '', type: 'feedback', timestamp: new Date().toLocaleString(), isFeedbackRequest: true };

        // Strip the "how would you like to continue?" prompt and its buttons.
        // The conversation is over — leaving them on screen re-offers a choice the
        // user has already made and reads as if nothing happened.
        const choicePrompts = Object.values(CHOICE_PROMPT_BY_LANG);
        setMessages(prev => [
            ...prev.filter(msg =>
                !msg.hideMessage &&
                !msg.isInactivityPrompt &&
                msg.type !== 'choice_buttons' &&
                !(msg.type === 'gpt' && choicePrompts.includes(msg.message))
            ),
            feedbackMsg,
            linkMessage,
        ]);
        setShowPopup(true);
    };

    /**
     * Look the adviser up, then show the completion UI.
     *
     * @param knownAdviserId skip the lookup when the caller already has the id.
     *
     * NOTE: GET /chat/getAdvisorData is NOT a route on widget-server — the only
     * routes under /chat are start, resume, sendMessage, verifyIdentity,
     * saveCRSResults, whatsapp/start and feedback. That call 404s, `res.id` comes
     * back undefined, and the fallback then reads `expertData.id`, which is not a
     * field on expertData either. So without an id passed in, this produces no
     * link at all — which is exactly why finishing an assessment left the user
     * with no way through to the platform. Callers should pass the id; the fetch
     * is kept only as a last resort in case the route is added later.
     */
    const completeConversation = async (knownAdviserId) => {
        stopActivityMonitoring();

        if (knownAdviserId) {
            showConversationCompleteUi(knownAdviserId, selectedLanguage);
            return true;
        }

        try {
            const response = await fetch(base_url + '/chat/getAdvisorData', {
                method: 'GET',
                headers: {
                    'X-Conversation-ID': localStorage.getItem('imavenconvoid'),
                    'x-apikey': apikey,
                    'X-Color-Scheme': getCurrentColorScheme(),
                },
            });
            if (!response.ok) throw new Error(`getAdvisorData returned ${response.status}`);
            const res = await response.json();
            if (!res?.id) throw new Error('getAdvisorData returned no adviser id');
            showConversationCompleteUi(res.id, res.detectedLanguage);
            return true;
        } catch (error) {
            console.error('Failed to get advisor data:', error);
            if (expertData && expertData.id) {
                showConversationCompleteUi(expertData.id, selectedLanguage);
                return true;
            }
            return false;
        }
    };

    // Remove internal system markers (CONVERSATION:[DONE], LANGUAGE:[xx], SUMMARY:)
    // from an AI message so the user sees the human-readable summary but not the
    // machine control tokens.
    const stripSystemMarkers = (html) => {
        if (typeof html !== 'string') return '';
        let out = html;
        out = out.replace(/<p>\s*CONVERSATION:\s*\[DONE\]?\s*<\/p>/gi, '');
        out = out.replace(/CONVERSATION:\s*\[DONE\]?/gi, '');
        // Both marker forms (bracketed or not) — shared with the bubble renderer.
        out = stripEngineMarkers(out);
        out = out.replace(/<p>\s*(<strong>)?\s*SUMMARY:\s*(<\/strong>)?[^<]*<\/p>/gi, '');
        out = out.replace(/<p>\s*<\/p>/gi, '').replace(/(\s*<br\s*\/?>\s*)+$/gi, '').trim();
        return out;
    };

    const updateLastMessage = (message) => {
        // Check if message contains CONVERSATION:[DONE] (with colon)
        // Bracketed or not: the model sometimes drops the brackets (CONVERSATION:DONE).
        if (typeof message === 'string' && /CONVERSATION:\s*\[?\s*DONE/.test(message)) {
            console.log("Detected conversation done marker - stopping all monitoring");

            stopActivityMonitoring();

            // If the message was already processed and set, don't process again
            if (messages.length > 0 && messages[messages.length - 1].isConversationDone) {
                return;
            }
            
            // Mark the entire message to be hidden, but keep the original text in case we need it
            setMessages((prevMessages) => {
                // Filter out any existing inactivity messages
                const filteredMessages = prevMessages.filter(msg => !msg.isInactivityPrompt);
                const newMessages = [...filteredMessages];
                
                if (newMessages.length > 0) {
                    // Show the AI's final summary (with system markers stripped) instead
                    // of hiding it — the user should see the assessment, not just a link.
                    const cleaned = stripSystemMarkers(message);
                    const hasText = cleaned.replace(/<[^>]*>/g, '').trim().length > 0;
                    newMessages[newMessages.length - 1] = {
                        ...newMessages[newMessages.length - 1],
                        originalMessage: message, // Store original for reference if needed
                        message: hasText ? cleaned : "",
                        isConversationDone: true,
                        hideMessage: !hasText // hide only if nothing but markers remained
                    };
                }
                return newMessages;
            });
            
            // Trigger the completion UI (adviser link, feedback prompt, popup).
            // Shared with the finishFlag path and the assessment path so all
            // three ways of finishing land the user in the same place.
            setTimeout(() => {
                if (!showPopup) completeConversation();
            }, 100);
        } else {
            // Normal message handling for non-CONVERSATION:[DONE] messages.
            // Force type 'gpt' so a filled assistant reply always renders as a normal
            // left-aligned bubble, even if it started as a 'system' loading placeholder.
            setMessages((prevMessages) => {
                const newMessages = [...prevMessages];
                newMessages[newMessages.length - 1] = {
                    ...newMessages[newMessages.length - 1],
                    message: message,
                    type: 'gpt',
                };
                return newMessages;
            });
        }
    }

    const handleInputChange = (event) => {
        setCurrentMessage(event.target.value);
    }

    const uploadDoc = () => {
        uploadRef.current.click()
    }

    const handleFileChange = async (event) => {
        setLoadingMessage(true);
        
        // Reset inactivity timer when user uploads a file
        updateUserActivityTimestamp();
        
        try {
            const files = event.target.files;
            if (files.length === 0)
                return;

            // Remove any inactivity messages before processing the file upload
            const filteredMessages = messages.filter(msg => !msg.isInactivityPrompt);

            const formData = new FormData();
            let fileNames = [];

            for (let i = 0; i < files.length; i++) {
                formData.append('files', files[i]);
                fileNames.push(files[i].name);  // Collect file names
            }

            const userMessage = `File uploaded: ${fileNames.join(", ")}`;
            const newMessage = { message: userMessage, type: 'user', timestamp: new Date().toLocaleString() };
            let temp = [...filteredMessages, newMessage];
            setMessages(temp);

            // Track uploaded documents for progress sidebar
            const newDocs = Array.from(files).map(file => ({
                name: file.name,
                size: file.size,
                uploadedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            }));
            setUploadedDocuments(prev => [...prev, ...newDocs]);
            setConversationStep('documents');

            if (window.plausible) {
                const fileCount = files.length;
                const totalSize = Array.from(files).reduce((acc, file) => acc + file.size, 0);
                const fileTypes = Array.from(new Set(
                    Array.from(files).map(file => file.type)
                )).join(', ');

                const now = new Date();
                const paddedHour = String(now.getHours()).padStart(2, '0');

                window.plausible('FileUpload', {
                    props: {
                        numberOfFiles: fileCount,
                        fileTypes: fileTypes,
                        totalSizeKB: Math.round(totalSize / 1024),
                        messageCount: filteredMessages.length,
                        timeOfDay: paddedHour
                    }
                });
            }

            const response = await fetch(base_url + '/upload', {
                method: 'POST',
                body: formData,
                credentials: 'include',
                headers: {
                    'X-Conversation-ID': localStorage.getItem('imavenconvoid'),
                    'X-Thread-ID': localStorage.getItem('imavenconvoid'),
                    'X-Directive-ID': localStorage.getItem('directiveId'),
                    'x-apikey': apikey,
                    'X-Color-Scheme': getCurrentColorScheme() // Add color scheme to headers
                }
            });

            if (response.ok) {
                const gptMessage = { message: "", type: 'gpt', timestamp: new Date().toLocaleString() };
                temp.push(gptMessage);
                setMessages(temp);
                setLoadingMessage(true);

                const aiResponse = await fetch(base_url + '/upload/sendFileData', {
                    body: JSON.stringify({ flag: true }),
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Conversation-ID': localStorage.getItem('imavenconvoid'),
                        'X-Thread-ID': localStorage.getItem('imavenconvoid'),
                        'X-Directive-ID': localStorage.getItem('directiveId'),
                        'x-apikey': apikey,
                        'X-Color-Scheme': getCurrentColorScheme() // Add color scheme to headers
                    }
                });

                // Try to parse as JSON, handle both success and error cases
                let data;
                const contentType = aiResponse.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    data = await aiResponse.json();
                } else {
                    // If not JSON, get the text and wrap it
                    const textResponse = await aiResponse.text();
                    data = { response: textResponse, error: !aiResponse.ok };
                }

                if (!aiResponse.ok) {
                    console.error('File data send error:', data);
                    updateLastMessage(data.response || data.message || 'Error processing file');
                } else {
                    updateLastMessage(data.response);
                    // Reset consecutive errors on successful file upload response
                    consecutiveErrorsRef.current = 0;
                }
            }
        } catch (error) {
            handleError(error);
            console.log("File upload error: ", error);
            updateLastMessage(error);
        } finally {
            setLoadingMessage(false);
        }
    };

    const closeChat = () => {
        if (window.plausible) {
            window.plausible("ChatClosed", {
                props: {
                    totalMessages: messages.length,
                    userMessages: messages.filter(m => m.type === 'user').length,
                    duration: Math.round((Date.now() - window._chatStartTime) / 60000),
                    completionType: messages.some(m => m.message?.includes('meeting-link')) ? 'closed_with_link' : 'closed_before_link',
                    lastMessageType: messages[messages.length - 1]?.type || 'none'
                }
            });
        }
        window.parent.postMessage({ action: 'closeChat' }, '*');
    };

    const openEmojiPicker = () => {
        // If we are inside an iframe, we manage the picker locally. Otherwise, we can still notify the parent (no-op in most cases).
        if (!isIframe) {
            window.parent.postMessage({ action: 'openEmojiPicker' }, '*');
        }
        setEmojiPickerOn(prev => !prev);
    }

    const emojiSelected = (emoji) => {
        console.log(emoji);
        // Update to use functional state update to ensure we have the latest state
        setCurrentMessage(prevMessage => prevMessage + emoji.native);
        // Don't close the picker automatically after selection
        // This allows selecting multiple emojis
    }

    const handleMessagesContainerClick = (e) => {
        const meetingLink = e.target.closest('#meeting-link');
        if (meetingLink) {
            e.preventDefault();
            const href = meetingLink.getAttribute('data-href');
            const adviserId = meetingLink.dataset.adviserid;

            // Stop all activity monitoring
            clearInterval(userInactivityTimerRef.current);
            clearTimeout(inactivityTimerRef.current);
            
            // Remove event listeners that monitor activity
            document.removeEventListener('keydown', handleKeyboardActivity);

            if (window.plausible) {
                window.plausible("ChatCompleted", {
                    props: {
                        adviserId: adviserId,
                        totalMessages: messages.length - 2,
                        userMessages: messages.filter(m => m.type === 'user').length,
                        duration: Math.round((Date.now() - window._chatStartTime) / 60000),
                        completionType: 'advisor_link'
                    },
                    callback: () => {
                        window.open(href, '_blank');
                    },
                });
            } else {
                window.open(href, '_blank');
            }
        }
    };

    // Track chat window close and send drop signal
    useEffect(() => {
        window._chatStartTime = Date.now();

        const handleBeforeUnload = () => {
            if (window.plausible) {
                window.plausible("ChatEnded", {
                    props: {
                        totalMessages: messages.length,
                        userMessages: messages.filter(m => m.type === 'user').length,
                        duration: Math.round((Date.now() - window._chatStartTime) / 60000),
                        completionType: messages.some(m => m.message?.includes('meeting-link')) ? 'abandoned_with_link' : 'abandoned_before_link',
                        lastMessageType: messages[messages.length - 1]?.type || 'none'
                    }
                });
            }
            
            // Send drop signal if conversation was started but not finished
            const hasUserMessages = messages.some(m => m.type === 'user');
            const isFinished = messages.some(m => m.isConversationDone || m.message?.includes('meeting-link'));
            if (hasUserMessages && !isFinished) {
                markSessionDropped(messages.length, conversationLanguage);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [messages, conversationLanguage]);

    // Track errors
    const handleError = (error) => {
        if (window.plausible) {
            window.plausible('ChatError', {
                props: {
                    errorMessage: error.toString(),
                    messageCount: messages.length
                }
            });
        }
        
        // Increment consecutive errors counter
        consecutiveErrorsRef.current += 1;
        
        // If we've had multiple consecutive errors, show the error popup
        if (consecutiveErrorsRef.current >= 2) {
            createErrorPopup(error.name || 'consecutive_errors');
            consecutiveErrorsRef.current = 0; // Reset after showing popup
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                emojiPickerOn &&
                !event.target.closest('.emoji-picker-wrapper') &&
                !event.target.closest('.em-emoji-picker')
            ) {
                setEmojiPickerOn(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [emojiPickerOn]);

    // Add toggle sidebar function
    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    // Add handlePopupClick function
    const handlePopupClick = () => {
        // Stop all activity monitoring
        clearInterval(userInactivityTimerRef.current);
        clearTimeout(inactivityTimerRef.current);
        
        // Remove event listeners that monitor activity
        document.removeEventListener('keydown', handleKeyboardActivity);
        
        if (window.plausible) {
            window.plausible("ChatCompleted", {
                props: {
                    adviserId: popupData.adviserId,
                    totalMessages: messages.length - 2,
                    userMessages: messages.filter(m => m.type === 'user').length,
                    duration: Math.round((Date.now() - window._chatStartTime) / 60000),
                    completionType: 'advisor_popup'
                },
                callback: () => {
                    window.open(popupData.href, '_blank');
                    setShowPopup(false);
                },
            });
        } else {
            window.open(popupData.href, '_blank');
            setShowPopup(false);
        }
    };

    // Helper function to determine the best supported audio MIME type
    const getSupportedMimeType = () => {
        const types = [
            'audio/webm',
            'audio/mp4',
            'audio/ogg',
            'audio/wav',
            'audio/webm;codecs=opus',
            'audio/webm;codecs=pcm',
            'audio/ogg;codecs=opus'
        ];

        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                console.log(`Using MIME type: ${type}`);
                return type;
            }
        }

        console.warn("No preferred MIME types supported, using default");
        return 'audio/webm'; // Default fallback
    };

    // Format recording time as mm:ss
    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // Toggle voice recording with special handling for mobile devices
    const toggleRecording = async () => {
        try {
            if (isRecording) {
                // Stop recording
                if (mediaRecorder && mediaRecorder.state === 'recording') {
                    mediaRecorder.stop();
                    setIsRecording(false);
                    // Clear the recording timer
                    setRecordingTime(0);

                    // Clear any interval timers
                    if (mediaRecorder.timerInterval) {
                        clearInterval(mediaRecorder.timerInterval);
                    }
                }
            } else {
                // Start recording
                setAudioChunks([]);

                // Reset transcription state
                setIsTranscribing(false);
                setTranscriptionStatus('');

                // Request microphone permission with specific options for mobile
                const constraints = {
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true
                    }
                };

                const stream = await navigator.mediaDevices.getUserMedia(constraints);

                // Determine best supported format
                const mimeType = getSupportedMimeType();
                setRecordingMimeType(mimeType);
                console.log(`Using MIME type: ${mimeType} for recording`);

                // Create and configure media recorder with better error handling
                const recorder = new MediaRecorder(stream, {
                    mimeType,
                    audioBitsPerSecond: 128000 // Ensure consistent quality
                });

                // Set up data handling
                let chunks = [];
                recorder.ondataavailable = (e) => {
                    if (e.data.size > 0) {
                        chunks.push(e.data);
                        setAudioChunks([...chunks]);
                        console.log(`Received audio chunk: ${e.data.size} bytes`);
                    }
                };

                // Handle recording stop
                recorder.onstop = async () => {
                    console.log("Recording stopped");

                    // Mobile devices might need extra time to process
                    setTimeout(() => {
                        // Get all tracks from stream and stop them
                        stream.getTracks().forEach(track => {
                            track.stop();
                            console.log(`Stopped audio track: ${track.kind}`);
                        });

                        // Process audio only if we have chunks
                        if (chunks.length > 0) {
                            console.log(`Processing ${chunks.length} audio chunks`);
                            const audioBlob = new Blob(chunks, { type: mimeType });
                            console.log(`Created audio blob: ${audioBlob.size} bytes`);

                            // Check if recording is too short
                            if (audioBlob.size < 1000) {
                                console.log("Recording too short");
                                setTranscriptionStatus('Recording too short');
                                setTimeout(() => setTranscriptionStatus(''), 3000);
                                return;
                            }

                            // Begin transcription
                            transcribeAudio(audioBlob);
                        } else {
                            console.error("No audio data collected");
                            setTranscriptionStatus('No audio recorded');
                            setTimeout(() => setTranscriptionStatus(''), 3000);
                        }
                    }, 300); // Short delay to ensure all processing is complete
                };

                // Extra error handling for mobile devices
                recorder.onerror = (event) => {
                    console.error("MediaRecorder error:", event);
                    setTranscriptionStatus('Recording error');
                    setTimeout(() => setTranscriptionStatus(''), 3000);

                    // Clean up on error
                    stream.getTracks().forEach(track => track.stop());
                    setIsRecording(false);
                };

                // Start the actual recording with smaller timeslice for more reliable data collection on mobile
                recorder.start(100); // Get data every 100ms for smoother UX
                setMediaRecorder(recorder);
                setIsRecording(true);

                // Set up timer for recording duration with safeguards for mobile interruptions
                let seconds = 0;
                let lastUpdate = Date.now();

                const timerInterval = setInterval(() => {
                    const now = Date.now();
                    // Check if more than 2 seconds have passed since last update
                    // This helps detect when device suspends JavaScript execution
                    if (now - lastUpdate > 2000) {
                        const missedSeconds = Math.floor((now - lastUpdate) / 1000);
                        seconds += missedSeconds;
                        console.log(`Detected time gap: ${missedSeconds}s`);
                    }

                    lastUpdate = now;
                    seconds++;
                    setRecordingTime(seconds);

                    // Auto-stop after 2 minutes to prevent excessive large files
                    if (seconds >= 120) {
                        clearInterval(timerInterval);
                        console.log("Auto-stopping after 120 seconds");
                        if (recorder.state === 'recording') {
                            recorder.stop();
                            setIsRecording(false);
                        }
                    }

                    // Check if recorder is still recording
                    if (recorder.state !== 'recording') {
                        clearInterval(timerInterval);
                        console.log("Recorder no longer recording, clearing timer");
                    }
                }, 1000);

                // Store timer in mediaRecorder object for cleanup
                recorder.timerInterval = timerInterval;

                // Detect page visibility changes which can affect recording on mobile
                const handleVisibilityChange = () => {
                    if (document.hidden && recorder.state === 'recording') {
                        console.log("Page hidden, ensuring recording continues");
                        // On some devices, might need to do something here to keep recording active
                    }
                };

                document.addEventListener('visibilitychange', handleVisibilityChange);

                // Store cleanup function
                recorder.visibilityCleanup = () => {
                    document.removeEventListener('visibilitychange', handleVisibilityChange);
                };
            }
        } catch (error) {
            console.error("Error toggling recording:", error);
            setTranscriptionStatus('Microphone access error');
            setTimeout(() => setTranscriptionStatus(''), 3000);
            setIsRecording(false);
        }
    };

    // Function to handle audio transcription with mobile-specific handling
    const transcribeAudio = async (audioBlob) => {
        try {
            setIsTranscribing(true);
            setTranscriptionStatus('Preparing audio...');

            console.log("Starting transcription for blob size:", audioBlob.size);

            // Create FormData for upload
            const formData = new FormData();
            const fileExt = recordingMimeType.split('/')[1] || 'webm';
            formData.append('audio', audioBlob, `recording.${fileExt}`);

            setTranscriptionStatus('Transcribing with Whisper...');

            // Send to server for transcription with timeout for mobile networks
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

            try {
                // Get expert ID for billing
                const expertId = expertData?.id || expertData?.expertId || expertData?._id || '';
                
                const response = await fetch(`${base_url}/api/transcribe`, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'X-Conversation-ID': localStorage.getItem('imavenconvoid'),
                        'X-Thread-ID': localStorage.getItem('imavenconvoid'),
                        'X-Asst-ID': localStorage.getItem('asstId'),
                        'X-Directive-ID': localStorage.getItem('directiveId'),
                        'X-Expert-ID': expertId,
                        'x-apikey': apikey,
                        'X-Color-Scheme': getCurrentColorScheme()
                    },
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                // Handle response
                if (!response.ok) {
                    throw new Error(`Server responded with ${response.status}`);
                }

                const data = await response.json();
                console.log("Transcription received:", data);

                // Add transcription to input field
                if (data.transcription && data.transcription.trim()) {
                    setCurrentMessage(prev => {
                        const prefix = prev.trim() ? `${prev.trim()} ` : '';
                        return `${prefix}${data.transcription.trim()}`;
                    });

                    // Focus input field after transcription
                    setTimeout(() => {
                        if (inputRef.current) {
                            inputRef.current.focus();
                        }
                    }, 300);

                    setTranscriptionStatus('');
                } else {
                    setTranscriptionStatus('No speech detected');
                    setTimeout(() => setTranscriptionStatus(''), 3000);
                }
            } catch (fetchError) {
                if (fetchError.name === 'AbortError') {
                    console.error("Transcription request timed out");
                    setTranscriptionStatus('Transcription timed out');
                } else {
                    throw fetchError;
                }
            }
        } catch (error) {
            console.error("Transcription error:", error);
            setTranscriptionStatus('Transcription failed');
            setTimeout(() => setTranscriptionStatus(''), 3000);
        } finally {
            setIsTranscribing(false);
        }
    };

    // Enhanced cleanup function to better handle mobile scenarios
    useEffect(() => {
        return () => {
            if (mediaRecorder) {
                if (mediaRecorder.state === 'recording') {
                    mediaRecorder.stop();
                }

                if (mediaRecorder.timerInterval) {
                    clearInterval(mediaRecorder.timerInterval);
                }

                if (mediaRecorder.visibilityCleanup) {
                    mediaRecorder.visibilityCleanup();
                }

                // Ensure all tracks are stopped
                if (mediaRecorder.stream) {
                    mediaRecorder.stream.getTracks().forEach(track => track.stop());
                }
            }
        };
    }, [mediaRecorder]);

    // Function to reset inactivity timer
    const resetInactivityTimer = () => {
        if (disableInactivityPrompt) return;
        
        clearTimeout(inactivityTimerRef.current);
        
        // Don't reset the UI state here, only reset the timer
        // We'll let the user explicitly dismiss the prompt instead
        
        inactivityTimerRef.current = setTimeout(() => {
            // Only show the prompt if there are messages and we're not loading
            if (messages.length > 0 && !loadingMessage) {
                setShowAnimatedIcon(true);
            }
        }, 20000); // 20 seconds for production
    };

    // Start inactivity timer after each AI response
    useEffect(() => {
        if (!disableInactivityPrompt && messages.length > 0 && !loadingMessage) {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage && lastMessage.type === 'gpt') {
                resetInactivityTimer();
            }
        }
    }, [messages, loadingMessage, disableInactivityPrompt]);

    // Reset timer on user activity but don't hide UI elements
    useEffect(() => {
        const handleActivity = () => {
            // Only reset the timer, don't hide UI elements
            if (!showAnimatedIcon && !showInactivityPrompt) {
                resetInactivityTimer();
            }
        };

        // Add event listeners for user activity
        document.addEventListener('mousemove', handleActivity);
        document.addEventListener('keypress', handleActivity);
        document.addEventListener('click', handleActivity);

        // Add document click handler to dismiss prompt when clicking outside
        const handleDocumentClick = (e) => {
            // If prompt is showing and click is outside prompt and not on the icon
            if (showInactivityPrompt) {
                const promptContainer = document.querySelector('.inactivity-prompt-container');
                const icon = document.querySelector('.inactivity-icon');
                
                // Prevent immediate closing when clicking the icon or inside the prompt
                if (promptContainer && !promptContainer.contains(e.target) && 
                    !(icon && icon.contains(e.target))) {
                    console.log("Clicking outside prompt - closing");
                    setShowInactivityPrompt(false);
                }
            }
        };
        
        document.addEventListener('click', handleDocumentClick);

        return () => {
            document.removeEventListener('mousemove', handleActivity);
            document.removeEventListener('keypress', handleActivity);
            document.removeEventListener('click', handleActivity);
            document.removeEventListener('click', handleDocumentClick);
            clearTimeout(inactivityTimerRef.current);
        };
    }, [disableInactivityPrompt, showAnimatedIcon, showInactivityPrompt]);

    // Update timer when currentMessage changes
    useEffect(() => {
        if (currentMessage.trim().length > 0) {
            // If user starts typing, hide the prompts
            setShowAnimatedIcon(false);
            setShowInactivityPrompt(false);
            resetInactivityTimer();
        }
    }, [currentMessage]);

    // Handle showing full prompt when icon is clicked/hovered
    const handleIconInteraction = () => {
        // Disabled - using new message system instead
        return;
    };

    // Handle checkbox change
    const handleCheckboxChange = (e) => {
        setDisableInactivityPrompt(e.target.checked);
        if (e.target.checked) {
            setShowInactivityPrompt(false);
            setShowAnimatedIcon(false);
            clearTimeout(inactivityTimerRef.current);
            // Removed localStorage.setItem to only disable for current session
        } else {
            // Removed localStorage.removeItem as we're not using localStorage anymore
        }
    };

    // Remove localStorage check on mount since we don't want to persist between sessions
    // useEffect(() => {
    //     const savedPreference = localStorage.getItem('disableInactivityPrompt');
    //     if (savedPreference === 'true') {
    //         setDisableInactivityPrompt(true);
    //     }
    // }, []);

    // Add mobile-specific logic for icon hover/click
    const handleIconClick = () => {
        // Disabled - using new message system instead
        return;
    };

    const handleIconHover = () => {
        // Disabled - using new message system instead
        return;
    };

    const updateUserActivityTimestamp = () => {
        const hasUserMessages = messages.some(msg => msg.type === 'user');
        
        if (hasUserMessages) {
            lastUserActivityRef.current = Date.now();
            hasUserInteractedRef.current = true;
            
            if (showUserInactiveMessage) {
                setShowUserInactiveMessage(false);
            }
            
            setInactivityStage(0);
            lastFiredStageRef.current = 0;
            
            resetUserInactivityTimer();
        }
    };

    // Stage thresholds: time since last user activity (ms)
    const STAGE_THRESHOLDS = { 2: 300000, 3: 600000, 4: 900000 }; // 5min, 10min, 15min

    const resetUserInactivityTimer = () => {
        const hasUserMessages = messages.some(msg => msg.type === 'user');
        if (!hasUserMessages || messages.some(msg => msg.isConversationDone)) {
            return;
        }
        
        clearInterval(userInactivityTimerRef.current);
        
        userInactivityTimerRef.current = setInterval(() => {
            const inactiveTime = Date.now() - lastUserActivityRef.current;
            
            let targetStage = 0;
            if (inactiveTime >= STAGE_THRESHOLDS[4]) {
                targetStage = 4;
            } else if (inactiveTime >= STAGE_THRESHOLDS[3]) {
                targetStage = 3;
            } else if (inactiveTime >= STAGE_THRESHOLDS[2]) {
                targetStage = 2;
            }
            
            // Only fire if this is a new stage we haven't shown yet
            if (targetStage > 0 && targetStage > lastFiredStageRef.current) {
                lastFiredStageRef.current = targetStage;
                setInactivityStage(targetStage);
                setShowUserInactiveMessage(true);
            }
            
            // Stop polling after final stage has been shown for 30s
            if (lastFiredStageRef.current >= 4 && inactiveTime >= STAGE_THRESHOLDS[4] + 30000) {
                clearInterval(userInactivityTimerRef.current);
            }
        }, 15000);
    };

    // Function to pause the user inactivity timer
    const pauseUserInactivityTimer = () => {
        clearInterval(userInactivityTimerRef.current);
    };

    // Function to resume the user inactivity timer
    const resumeUserInactivityTimer = () => {
        lastUserActivityRef.current = Date.now(); // Reset the timestamp
        resetUserInactivityTimer();
    };

    // Pause inactivity timer during loading and resume after loading
    useEffect(() => {
        if (loadingMessage) {
            pauseUserInactivityTimer();
        } else {
            resumeUserInactivityTimer();
        }
    }, [loadingMessage]);

    // Initialize user inactivity timer with modified event listeners
    useEffect(() => {
        // We only want to track actual text input and voice recording as activity
        const handleKeyboardActivity = (e) => {
            // Only consider typing in the input field as activity
            if (e.target === inputRef.current) {
                updateUserActivityTimestamp();
            }
        };

        // Don't use general mouse/touch events as they don't indicate engagement
        document.addEventListener('keydown', handleKeyboardActivity);

        // Initial timer
        resetUserInactivityTimer();
        
        // Disable old inactivity prompt system
        setShowAnimatedIcon(false);
        setShowInactivityPrompt(false);
        setDisableInactivityPrompt(true);

        return () => {
            document.removeEventListener('keydown', handleKeyboardActivity);
            clearInterval(userInactivityTimerRef.current);
        };
    }, []);

    // Make handleKeyboardActivity available outside the useEffect scope
    const handleKeyboardActivity = (e) => {
        // Only consider typing in the input field as activity
        if (e.target === inputRef.current) {
            updateUserActivityTimestamp();
        }
    };

    // Update activity timestamp when input changes
    useEffect(() => {
        if (currentMessage.trim() !== '') {
            updateUserActivityTimestamp();
        }
    }, [currentMessage]);

    // Update activity timestamp when recording starts
    useEffect(() => {
        if (isRecording) {
            updateUserActivityTimestamp();
        }
    }, [isRecording]);

    // Update activity timestamp when transcribing
    useEffect(() => {
        if (isTranscribing) {
            updateUserActivityTimestamp();
        }
    }, [isTranscribing]);

    // Reset inactivity timer when loading state changes
    useEffect(() => {
        if (!loadingMessage) {
            // Don't update activity timestamp, just reset the timer
            resetUserInactivityTimer();
        }
    }, [loadingMessage]);

    // Add message to messages when user is inactive
    const addInactivityMessage = () => {
        if (messages.some(msg => msg.isConversationDone)) {
            setShowUserInactiveMessage(false);
            return;
        }
        
        const hasUserMessages = messages.some(msg => msg.type === 'user');
        if (!hasUserMessages) {
            setShowUserInactiveMessage(false);
            return;
        }
        
        if (showUserInactiveMessage) {
            if (inactivityStage < 2 || inactivityStage > 4) {
                setShowUserInactiveMessage(false);
                return;
            }

            const messageContent = getInactivityMessage(inactivityStage, conversationLanguage);
            if (!messageContent) {
                setShowUserInactiveMessage(false);
                return;
            }
            
            const inactivityMessage = {
                message: messageContent,
                type: 'system',
                timestamp: new Date().toLocaleString(),
                isInactivityPrompt: true,
                inactivityStage: inactivityStage
            };
            
            if (!messages.some(msg => msg.isInactivityPrompt && msg.inactivityStage === inactivityStage)) {
                const filteredMessages = messages.filter(msg => !msg.isInactivityPrompt);
                setMessages([...filteredMessages, inactivityMessage]);
                
                setTimeout(() => {
                    if (listRef.current) {
                        listRef.current.scrollTop = listRef.current.scrollHeight;
                    }
                }, 100);
            }
            
            setShowUserInactiveMessage(false);
        }
    };

    // Show inactivity message when user is inactive
    useEffect(() => {
        if (showUserInactiveMessage && !loadingMessage) {
            addInactivityMessage();
        }
    }, [showUserInactiveMessage, inactivityStage, conversationLanguage]);

    // Add a function to handle the error popup click
    const handleErrorPopupClick = () => {
        // Stop all activity monitoring
        clearInterval(userInactivityTimerRef.current);
        clearTimeout(inactivityTimerRef.current);
        
        // Remove event listeners that monitor activity
        document.removeEventListener('keydown', handleKeyboardActivity);
        
        if (window.plausible) {
            window.plausible("ChatErrorCompleted", {
                props: {
                    adviserId: errorPopupData.adviserId,
                    totalMessages: messages.length,
                    userMessages: messages.filter(m => m.type === 'user').length,
                    duration: Math.round((Date.now() - window._chatStartTime) / 60000),
                    errorType: errorPopupData.errorType || 'unknown_error',
                    completionType: 'error_popup'
                },
                callback: () => {
                    window.open(errorPopupData.href, '_blank');
                    setShowErrorPopup(false);
                },
            });
        } else {
            window.open(errorPopupData.href, '_blank');
            setShowErrorPopup(false);
        }
    };

    // Add a function to create the error popup
    const createErrorPopup = (errorType = 'general') => {
        // Only show error popup if we have the expert data
        if (!expertData || !expertData.expertInfo) {
            console.error('Cannot create error popup without expert data');
            return;
        }
        
        // Create the adviser ID from expert data or fallback
        const adviserId = expertData.id || 'adviser_1';
        
        // Create conversation ID or fallback
        const conversationId = localStorage.getItem('imavenconvoid') || 'new_conversation';
        
        // Detect language or default to English
        const detectedLanguage = 'en';
        
        // Create the URL
        const href = `https://platform.imavenai.com/setMetting/${adviserId}/${conversationId}?lang=${detectedLanguage}&error=${errorType}`;
        
        // Set error popup data
        setErrorPopupData({
            href,
            adviserId,
            errorType
        });
        
        // Show the error popup
        setShowErrorPopup(true);
    };

    return (
        <WidgetWrapper>
            {loading ? (
                <LoadingOverlay>
                    <img src={LoadingIcon} alt="Loading..." width={'20px'} height={'20px'} />
                </LoadingOverlay>
            ) : (
                <FadeInWrapper $loaded={!loading}>
                    {error.length > 0 ? (
                        <div>{error}</div>
                    ) : isIframe ? (
                        <div style={{ width: "320px", borderRadius: "12px", height: '500px', bottom: '1', zIndex: '10002', backgroundColor: 'white', overflow: 'hidden', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                            <IframeHeader $scheme={getCurrentColorScheme()}>
                                <div style={{
                                    width: '100%', display: 'flex', height: '70%',
                                    justifyContent: 'space-between', alignItems: 'center'
                                }}>
                                    <LogoWrapper>
                                        <Logo url={expertData.widget?.logoUrl || ImavenIcon}></Logo>
                                        <strong>We're Here to Help!</strong>
                                    </LogoWrapper>
                                    <ExitButtonWrapper>
                                        <img onClick={closeChat} src={`${base_url}/icons/exist-icon?api_key=${apikey}`} />
                                    </ExitButtonWrapper>
                                </div>
                                <div style={{
                                    width: '100%', height: '25%',
                                    borderTop: "1px solid rgba(255,255,255,0.2)",
                                    display: 'flex', alignItems: 'center', lineHeight: '1'
                                }}>
                                    <span>&nbsp;&nbsp;Instant AI assistance.</span>
                                </div>
                            </IframeHeader>
                            <IFrameChatWindow scheme={getCurrentColorScheme()}>
                                {widgetPhase === 'language_select' ? (
                                    <LanguageSelector onSelect={handleLanguageSelect} scheme={getCurrentColorScheme()} />
                                ) : widgetPhase === 'crs' ? (
                                    <CRSCalculator onComplete={handleCRSComplete} onBack={handleCRSBack} scheme={getCurrentColorScheme()} language={selectedLanguage} />
                                ) : widgetPhase === 'crs_results' ? (
                                    <CRSResults result={crsResult} onContinueToIntake={handleContinueToIntakeFromCRS}
                                        onDecline={handleDeclineIntake} scheme={getCurrentColorScheme()} language={selectedLanguage} />
                                ) : oinpPhaseScreen ? (
                                    oinpPhaseScreen
                                ) : (
                                    <ChatArea
                                        darkMode={false} scheme={getCurrentColorScheme()} messages={messages}
                                        listRef={listRef} handleMessagesContainerClick={handleMessagesContainerClick}
                                        loadingMessage={loadingMessage} emojiPickerOn={emojiPickerOn}
                                        emojiSelected={emojiSelected} openEmojiPicker={openEmojiPicker}
                                        uploadRef={uploadRef} handleFileChange={handleFileChange} uploadDoc={uploadDoc}
                                        inputRef={inputRef} currentMessage={currentMessage} handleInputChange={handleInputChange}
                                        sendMessage={sendMessage} apikey={apikey} base_url={base_url}
                                        isRecording={isRecording} recordingTime={recordingTime} toggleRecording={toggleRecording}
                                        isTranscribing={isTranscribing} transcriptionStatus={transcriptionStatus} formatTime={formatTime}
                                        questionnaireProgress={questionnaireProgress}
                                        widgetPhase={widgetPhase} selectedLanguage={selectedLanguage} identificationComplete={identificationComplete}
                                        assessmentTemplates={assessmentTemplates}
                                        handleChooseAssessment={handleChooseAssessment} handleChooseIntake={handleChooseIntake}
                                        showPoweredBy={true} showTimestamp={true}
                                        isIframe={true} closeChat={closeChat}
                                        isMobile={false}
                                        feedbackState={feedbackState} onFeedbackThumb={handleFeedbackThumb}
                                        onFeedbackSubmit={handleFeedbackSubmit} feedbackComment={feedbackComment}
                                        setFeedbackComment={setFeedbackComment}
                                        otpProps={otpProps}
                                    />
                                )}
                            </IFrameChatWindow>
                        </div>
                    ) : isMobile ? (
                        <ChatPageMobile
                            darkMode={false}
                            colorScheme={getCurrentColorScheme()}
                            messages={messages}
                            expertData={expertData}
                            sidebarOpen={sidebarOpen}
                            toggleSidebar={toggleSidebar}
                            toggleMode={toggleMode}
                            listRef={listRef}
                            handleMessagesContainerClick={handleMessagesContainerClick}
                            loadingMessage={loadingMessage}
                            emojiPickerOn={emojiPickerOn}
                            data={data}
                            emojiSelected={emojiSelected}
                            uploadRef={uploadRef}
                            handleFileChange={handleFileChange}
                            uploadDoc={uploadDoc}
                            openEmojiPicker={openEmojiPicker}
                            inputRef={inputRef}
                            currentMessage={currentMessage}
                            handleInputChange={handleInputChange}
                            sendMessage={sendMessage}
                            apikey={apikey}
                            base_url={base_url}
                            isRecording={isRecording}
                            recordingTime={recordingTime}
                            toggleRecording={toggleRecording}
                            isTranscribing={isTranscribing}
                            transcriptionStatus={transcriptionStatus}
                            formatTime={formatTime}
                            conversationStep={conversationStep}
                            capturedInfo={capturedInfo}
                            uploadedDocuments={uploadedDocuments}
                            questionnaireProgress={questionnaireProgress}
                            widgetPhase={widgetPhase}
                            selectedLanguage={selectedLanguage}
                            crsResult={crsResult}
                            onLanguageSelect={handleLanguageSelect}
                            assessmentTemplates={assessmentTemplates}
                            onChooseAssessment={handleChooseAssessment}
                            onChooseIntake={handleChooseIntake}
                            onCRSComplete={handleCRSComplete}
                            onCRSBack={handleCRSBack}
                            onContinueToIntakeFromCRS={handleContinueToIntakeFromCRS}
                            onDeclineIntake={handleDeclineIntake}
                            oinpPhaseScreen={oinpPhaseScreen}
                            identificationComplete={identificationComplete}
                            otpProps={otpProps}
                        />
                    ) : (
                        /* ─── NEW DESKTOP: Expert Strip (left) + Chat Area (right) ─── */
                        <DesktopLayout $scheme={getCurrentColorScheme()}>
                            <ExpertSidebar
                                scheme={getCurrentColorScheme()}
                                expertData={expertData}
                                conversationStep={conversationStep}
                                questionnaireProgress={questionnaireProgress}
                            />

                            {widgetPhase === 'language_select' ? (
                                <ChatAreaContainer>
                                    <LanguageSelector onSelect={handleLanguageSelect} scheme={getCurrentColorScheme()} />
                                </ChatAreaContainer>
                            ) : widgetPhase === 'crs' ? (
                                <ChatAreaContainer>
                                    <CRSCalculator onComplete={handleCRSComplete} onBack={handleCRSBack} scheme={getCurrentColorScheme()} language={selectedLanguage} />
                                </ChatAreaContainer>
                            ) : widgetPhase === 'crs_results' ? (
                                <ChatAreaContainer>
                                    <CRSResults result={crsResult} onContinueToIntake={handleContinueToIntakeFromCRS}
                                        onDecline={handleDeclineIntake} scheme={getCurrentColorScheme()} language={selectedLanguage} />
                                </ChatAreaContainer>
                            ) : oinpPhaseScreen ? (
                                <ChatAreaContainer>
                                    {oinpPhaseScreen}
                                </ChatAreaContainer>
                            ) : (
                                <ChatArea
                                    darkMode={false} scheme={getCurrentColorScheme()} messages={messages}
                                    listRef={listRef} handleMessagesContainerClick={handleMessagesContainerClick}
                                    loadingMessage={loadingMessage} emojiPickerOn={emojiPickerOn}
                                    emojiSelected={emojiSelected} openEmojiPicker={openEmojiPicker}
                                    uploadRef={uploadRef} handleFileChange={handleFileChange} uploadDoc={uploadDoc}
                                    inputRef={inputRef} currentMessage={currentMessage} handleInputChange={handleInputChange}
                                    sendMessage={sendMessage} apikey={apikey} base_url={base_url}
                                    isRecording={isRecording} recordingTime={recordingTime} toggleRecording={toggleRecording}
                                    isTranscribing={isTranscribing} transcriptionStatus={transcriptionStatus} formatTime={formatTime}
                                    questionnaireProgress={questionnaireProgress}
                                    widgetPhase={widgetPhase} selectedLanguage={selectedLanguage} identificationComplete={identificationComplete}
                                    assessmentTemplates={assessmentTemplates}
                                    handleChooseAssessment={handleChooseAssessment} handleChooseIntake={handleChooseIntake}
                                    showPoweredBy={false} showTimestamp={true}
                                    isMobile={false}
                                    feedbackState={feedbackState} onFeedbackThumb={handleFeedbackThumb}
                                    onFeedbackSubmit={handleFeedbackSubmit} feedbackComment={feedbackComment}
                                    setFeedbackComment={setFeedbackComment}
                                    otpProps={otpProps}
                                />
                            )}
                        </DesktopLayout>
                    )}
                </FadeInWrapper>
            )}

            {showPopup && (
                <PopupOverlay onClick={() => setShowPopup(false)}>
                    <PopupContent $scheme={getCurrentColorScheme()} onClick={(e) => e.stopPropagation()}>
                        <strong>One last step</strong>
                        <p>
                            Your conversation has been saved. Click below to continue to
                            {expertData && expertData.expertName ? ` ${expertData.expertName}'s` : ' your adviser’s'} secure
                            page, where you can book a meeting or get an AI overview of your case.
                            Your details carry over automatically, so you won’t need to repeat anything.
                        </p>
                        <PopupButton $scheme={getCurrentColorScheme()} onClick={handlePopupClick}>
                            Continue
                        </PopupButton>
                    </PopupContent>
                </PopupOverlay>
            )}
            
            {showErrorPopup && (
                <PopupOverlay onClick={() => setShowErrorPopup(false)}>
                    <PopupContent $scheme={getCurrentColorScheme()} onClick={(e) => e.stopPropagation()}>
                        <strong>We're experiencing technical difficulties</strong>
                        <p>
                            It seems we're having some trouble communicating with our AI assistant at the moment.
                            To ensure you get the help you need, we recommend connecting directly with a human adviser.
                        </p>
                        <p>Your conversation details will be preserved and shared with the adviser so you won't need to repeat yourself.</p>
                        <PopupButton $scheme={getCurrentColorScheme()} onClick={handleErrorPopupClick}>
                            Connect with Human Adviser
                        </PopupButton>
                    </PopupContent>
                </PopupOverlay>
            )}
        </WidgetWrapper>
    );
};

export default ChatPage;
